/**
 * SidebarStep3 - Financial Data Entry (v2 Sidebar Layout)
 * 
 * This component wraps the existing Step3 form logic with the new sidebar layout.
 * It has three primary modes:
 *   1. Method Selection - Choose between Upload or Industry Templates
 *   2. Upload Mode - Before files are uploaded (drag & drop, file selection)
 *   3. Review Mode - After files are uploaded (data summary, navigation, editing)
 * 
 * EXACT same functionality as original Step3.tsx - only layout changes.
 */

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useCanonicalData } from './hooks/useCanonicalData';
import { getConfirmedEventDate, persistableEventStartDate, pickAutoEventStart } from './confirmedEventDate';
import { 
  Upload, 
  FileSpreadsheet, 
  CheckCircle, 
  AlertTriangle, 
  FileText,
  BarChart3,
  CreditCard,
  Folder,
  FileWarning,
  Plus,
  RefreshCw,
  Calendar,
  Check,
  Archive,
  LayoutTemplate,
  Sparkles,
  MessageSquare,
  Send,
  Mail,
  ChevronDown,
  X,
  Loader2,
  CheckCircle2,
  Eye,
  Download,
  Store
} from 'lucide-react';
import WizardV2Layout from './WizardV2Layout';
import type { QAItem } from './WizardSidebar';
import { hasEnoughDataForDetection } from '../../../utils/financialDataNormalizer';
import { pluralize } from '../../../utils/pluralize';
import { resolveK1Box, resolveGuaranteedPayments, resolveOrdinaryIncome, formatK1Value } from '../../../utils/k1Fields';
import api from '../../../utils/api';

// Import existing components for reuse
import UnifiedUploader from '../UnifiedUploader';
import TransactionViewer, { FlaggedTransaction } from '../../TransactionViewer';
import AnomalyBanner from '../AnomalyBanner';
import AnomalyResolver from '../AnomalyResolver';
import { resolveCoverageYear } from '../../../utils/resolveCoverageYear';
import EventCoveragePeriod from '../EventCoveragePeriod';
import TemplateSelector from '../../TemplateSelector';
import DocumentChecklist from '../DocumentChecklist';
import TaxReconciliation from '../TaxReconciliation';
import PDFViewerModal from '../../accountant/PDFViewerModal';
import RevenueSourcesCard, { RevenueSourcesData } from './RevenueSourcesCard';

/**
 * Build financial data structure from canonical_data periods
 * Uses the pre-computed aggregates from backend (gross_revenue, employee_wages, etc.)
 * If aggregates are $0 but transactions exist, returns data for display 
 * (actual recalculation should be done via backend API)
 */
/**
 * Build canonical_data structure from financial_data (reverse of above)
 * Used when anomaly detection needs canonical format but we only have financial_data
 */
const buildCanonicalFromFinancialData = (financialData: Record<string, any[]>): { periods: any[] } | null => {
  if (!financialData) return null;
  
  const periods: any[] = [];
  
  Object.entries(financialData)
    .filter(([key]) => key.startsWith('data_'))
    .forEach(([yearKey, months]) => {
      if (!Array.isArray(months)) return;
      
      months.forEach((monthData: any) => {
        periods.push({
          year: monthData.year || parseInt(yearKey.replace('data_', ''), 10),
          month: monthData.month,
          gross_revenue: monthData.revenue || 0,
          cogs: monthData.cogs || 0,
          employee_wages: monthData.employee_wages || monthData.payroll || 0,
          officer_compensation: monthData.officer_compensation || 0,
          fixed_costs: monthData.fixed_costs || 0,
          variable_costs: monthData.variable_costs || 0,
          net_profit: monthData.net_profit || 0,
          transactions: monthData.transactions || [],
        });
      });
    });
  
  // Sort periods by year then month
  periods.sort((a, b) => {
    if (a.year !== b.year) return a.year - b.year;
    return a.month - b.month;
  });
  
  return periods.length > 0 ? { periods } : null;
};

interface SidebarStep3Props {
  goToStep?: (step: number) => void;
  onNext: (data: any) => void;
  onBack: () => void;
  data: any;
  onDataUpdate?: (data: any) => void;
  onSave?: (explicitData?: any) => Promise<void>;
  isSaving?: boolean;
  lastSaved?: Date | null;
  initialSection?: string | null; // For deep linking from URL params
  highestCompletedStep?: number;
  backendCompletionPercent?: number;
  claimId?: string;
  chromeless?: boolean;
}

type ViewMode = 'upload' | 'files' | 'summary' | 'monthly' | 'transactions' | 'categories' | 'tax' | 'revenue_sources' | 'reconciliation' | 'anomalies' | 'template' | 'template_complete';

type EntryMethod = 'select' | 'upload' | 'template';

export default function SidebarStep3({
  onNext,
  onBack,
  data,
  onDataUpdate,
  onSave,
  isSaving,
  lastSaved,
  goToStep,
  initialSection,
  highestCompletedStep,
  backendCompletionPercent,
  claimId,
  chromeless
}: SidebarStep3Props) {
  // SSoT: Fetch canonical data + pre-built financial_data from backend
  const {
    canonicalData: hookCanonicalData,
    periods: hookPeriods,
    financialData: hookFinancialData,
    tax1065Files: hookTax1065Files,
    isLoading: isCanonicalLoading,
    refetch: refetchCanonical,
  } = useCanonicalData(claimId);

  // Determine initial mode based on existing data
  // Check if we have existing data from any source
  const hasExistingData = useMemo(() => {
    const hasFromFlags = data.fromSpreadsheet || data.fromCleanup || data.fromQuickBooks ||
                         data.fromTemplate || data.fromUnifiedUpload;
    const hasFinancialDataKeys = data.financial_data &&
                                 Object.keys(data.financial_data).some(k => k.startsWith('data_'));
    // Also check canonical data from SSoT endpoint (handles portal-uploaded files)
    const hasCanonicalPeriods = hookPeriods && hookPeriods.length > 0;
    const hasCanonicalFiles = hookCanonicalData?.uploaded_files_count > 0 || hookCanonicalData?.metadata?.total_files > 0;
    const result = hasFromFlags || hasFinancialDataKeys || hasCanonicalPeriods || hasCanonicalFiles;
    console.log('🔍 hasExistingData:', result, { hasFromFlags, hasFinancialDataKeys, hasCanonicalPeriods, hasCanonicalFiles, financial_data_keys: Object.keys(data.financial_data || {}) });
    return result;
  }, [data.fromSpreadsheet, data.fromCleanup, data.fromQuickBooks, data.fromTemplate, data.fromUnifiedUpload, data.financial_data, hookPeriods, hookCanonicalData]);

  // Entry method selection state
  const [entryMethod, setEntryMethod] = useState<EntryMethod>(
    hasExistingData ? 'upload' : 'select'
  );

  // When canonical data loads async (portal-uploaded files), switch from method selection to data view
  const hasAutoSwitchedToData = useRef(false);
  useEffect(() => {
    if (hasExistingData && !hasAutoSwitchedToData.current) {
      hasAutoSwitchedToData.current = true;
      setEntryMethod('upload');
      setActiveView('summary');
      setIsUploadMode(false);
    }
  }, [hasExistingData]);

  // While canonical is loading and we don't have local data yet, suppress method-selection flash
  const shouldShowMethodSelection = entryMethod === 'select' && !hasExistingData && !isCanonicalLoading;
  
  // Template data state
  const [appliedTemplate, setAppliedTemplate] = useState<any>(data.applied_template || null);

  // Determine initial view - prioritize deep link section if provided and data exists
  const getInitialView = (): ViewMode => {
    if (initialSection && hasExistingData) {
      const validSections: ViewMode[] = ['summary', 'monthly', 'transactions', 'categories', 'tax', 'revenue_sources', 'reconciliation', 'anomalies'];
      if (validSections.includes(initialSection as ViewMode)) {
        console.log('🔗 Deep link: Setting initial view to', initialSection);
        return initialSection as ViewMode;
      }
    }
    if (data.fromTemplate) return 'template_complete';
    if (hasExistingData) return 'summary';
    return 'upload';
  };

  // State management (replicated from original Step3.tsx)
  const [isUploadMode, setIsUploadMode] = useState(!hasExistingData);
  const [activeView, setActiveView] = useState<ViewMode>(getInitialView());
  
  // Financial data state — backend builds this, no client-side transformation
  const [aiExtractedData, setAiExtractedData] = useState<any>(() => {
    // Use existing financial_data from backend (saved on claim document by canonical cache rebuild)
    if (data.financial_data && Object.keys(data.financial_data).length > 0) {
      return data.financial_data;
    }
    return null;
  });
  const [unifiedUploadData, setUnifiedUploadData] = useState<any>(data.unified_upload_data || null);
  const [tax1065Files, setTax1065Files] = useState<any[]>(data.tax1065Files || []);
  const [plFiles, setPlFiles] = useState<any[]>(data.plFiles || []);
  const [uploadedDocuments, setUploadedDocuments] = useState<any[]>(data.uploadedDocuments || []);
  const [viewingFile, setViewingFile] = useState<any | null>(null);
  const [viewingFileUrl, setViewingFileUrl] = useState<string | null>(null);

  // SSoT: Use pre-built data from backend canonical-data endpoint.
  // Backend returns financial_data and tax1065Files already built — NO client-side transformation.
  useEffect(() => {
    if (!hookCanonicalData?.periods?.length) return;

    // Seed canonical data for TransactionViewer
    setUnifiedUploadData((prev: any) => ({
      ...prev,
      canonical_data: hookCanonicalData,
    }));

    // Use backend-built financial_data (no buildFinancialDataFromCanonical)
    if (hookFinancialData && Object.keys(hookFinancialData).length > 0) {
      setAiExtractedData(hookFinancialData);
    }

    // Use backend-built tax1065Files
    if (hookTax1065Files?.length > 0 && tax1065Files.length === 0) {
      setTax1065Files(hookTax1065Files);
      if (onDataUpdate) onDataUpdate({ tax1065Files: hookTax1065Files });
    }
  }, [hookCanonicalData, hookFinancialData, hookTax1065Files]);

  // Event coverage state
  // Prefill from the confirmed (document-backed) EI date; a prefilled value is NOT persisted
  // unless the user edits it (startDatePrefilled), so the stored date never shadows EI.
  const [eventStartDate, setEventStartDate] = useState(data.event_start_date || getConfirmedEventDate(data) || '');
  const [startDatePrefilled, setStartDatePrefilled] = useState(!data.event_start_date && !!getConfirmedEventDate(data));
  const [eventEndDate, setEventEndDate] = useState(data.event_end_date || '');
  const [femaDeclarationNumber, setFemaDeclarationNumber] = useState(data.fema_declaration_number || '');
  
  // Anomaly state - MUST be declared before useEffect that uses it
  const [highlightedTransactionId, setHighlightedTransactionId] = useState<string | null>(null);
  const [txnSourceFilter, setTxnSourceFilter] = useState<'all' | 'bank' | 'credit_card'>('all');
  const [resolvedAnomalies, setResolvedAnomalies] = useState<Array<{id: string; note: string; resolved_at: string}>>(
    data.resolved_anomalies || []
  );
  const [cachedAnomalyAnalysis, setCachedAnomalyAnalysis] = useState<{
    anomalies: any[];
    dataHash: string;
    analyzedAt: string;
  } | null>(data.cached_anomaly_analysis || null);
  
  // Auto-detect event dates from anomaly analysis when analysis completes
  useEffect(() => {
    // Skip if dates already set
    if (eventStartDate && eventEndDate) return;
    
    // Need cached anomaly analysis to detect dates
    const anomalies = cachedAnomalyAnalysis?.anomalies || [];
    if (anomalies.length === 0) return;
    
    // Find significant revenue drops (>40%, not January seasonal)
    const revenueDrops = anomalies.filter((a: any) => 
      a.anomaly_type === 'revenue_drop' && 
      a.affected_period &&
      !a.affected_period.endsWith('-01') &&
      Math.abs(a.deviation_percent || 0) > 40
    );
    
    if (revenueDrops.length > 0) {
      const biggestDrop = revenueDrops.reduce((max: any, curr: any) => 
        Math.abs(curr.deviation_percent || 0) > Math.abs(max.deviation_percent || 0) ? curr : max
      );
      
      const [year, month] = biggestDrop.affected_period.split('-').map(Number);
      const startDate = `${year}-${String(month).padStart(2, '0')}-01`;
      const endDate = `${year + 1}-${String(month).padStart(2, '0')}-01`;
      
      const updates: Record<string, string> = {};
      if (!eventStartDate && !getConfirmedEventDate(data)) {
        setEventStartDate(startDate);
        updates.event_start_date = startDate;
        console.log(`📅 Auto-detected loss event start: ${startDate}`);
      }
      if (!eventEndDate) {
        setEventEndDate(endDate);
        updates.event_end_date = endDate;
        console.log(`📅 Auto-detected loss event end: ${endDate}`);
      }
      if (onDataUpdate && Object.keys(updates).length > 0) {
        onDataUpdate(updates);
      }
    }
  }, [cachedAnomalyAnalysis?.anomalies, eventStartDate, eventEndDate, onDataUpdate]);

  // Client clarification state
  const [flaggedTransactions, setFlaggedTransactions] = useState<FlaggedTransaction[]>(
    data.flagged_transactions || []
  );
  const [showClarificationPanel, setShowClarificationPanel] = useState(false);
  const [sendingClarification, setSendingClarification] = useState(false);
  const [clarificationSent, setClarificationSent] = useState(false);
  const [clientEmail, setClientEmail] = useState(data.client_email || data.claimant_email || '');

  // Event date validation state
  const [eventDateValidation, setEventDateValidation] = useState<{
    is_valid: boolean;
    confidence: string;
    warnings: string[];
    suggestions: string[];
    auto_detected_date: string | null;
  } | null>(null);
  
  // Files view state - for showing uploaded S3 files
  const [s3Files, setS3Files] = useState<any[]>([]);
  const [filesLoading, setFilesLoading] = useState(true); // Start as loading to prevent flash
  const [reprocessingFileId, setReprocessingFileId] = useState<string | null>(null);
  
  // Inline validation/status messages (replacing alert() calls)
  const [validationError, setValidationError] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  // Recalculate aggregates state
  const [isRecalculating, setIsRecalculating] = useState(false);
  
  // Diagnostics state
  const [isDiagnosing, setIsDiagnosing] = useState(false);
  const [diagnosticResults, setDiagnosticResults] = useState<any>(null);
  const [showDiagnostics, setShowDiagnostics] = useState(false);

  // Run diagnostics on financial data
  const handleDiagnose = async () => {
    const draftId = claimId;
    if (!draftId) {
      console.error('No draft ID available for diagnostics');
      return;
    }
    
    setIsDiagnosing(true);
    try {
      console.log('🔍 Running diagnostics for draft:', draftId);
      const response = await api.get(`/api/v1/upload-savefirst/diagnostics/${draftId}`);
      
      setDiagnosticResults(response);
      setShowDiagnostics(true);
      console.log('✅ Diagnostics complete:', response);
    } catch (error) {
      console.error('Error running diagnostics:', error);
      setValidationError('Failed to run diagnostics. Please try again.');
    } finally {
      setIsDiagnosing(false);
    }
  };

  // Fix identified issues
  const handleFixIssues = async () => {
    const draftId = claimId;
    if (!draftId) return;
    
    setIsRecalculating(true);
    try {
      console.log('🔧 Fixing issues for draft:', draftId);
      const response = await api.post(`/api/v1/upload-savefirst/fix-issues/${draftId}`, {
        fix_types: ['aggregation', 'cogs']
      });
      
      if (response.success) {
        console.log('✅ Fixes applied:', response);
        setShowDiagnostics(false);
        setDiagnosticResults(null);
        // Reload to show updated data
        window.location.reload();
      } else {
        setValidationError(`Fix failed: ${response.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error fixing issues:', error);
      setValidationError('Failed to fix issues. Please try again.');
    } finally {
      setIsRecalculating(false);
    }
  };

  // Recalculate aggregates from transactions using backend logic
  const handleRecalculateAggregates = async () => {
    const draftId = claimId;
    if (!draftId) {
      console.error('No draft ID available for recalculation');
      return;
    }
    
    setIsRecalculating(true);
    try {
      console.log('📊 Recalculating aggregates for draft:', draftId);
      const response = await api.post('/api/v1/upload-savefirst/recalculate-aggregates', {
        draft_id: draftId
      });
      
      if (response.success) {
        console.log('✅ Recalculation complete:', response);
        
        // Reload the page to get fresh data
        // Or update the state directly if we have the updated canonical_data
        window.location.reload();
      } else {
        console.error('Recalculation failed:', response.message);
        setValidationError(`Recalculation failed: ${response.message}`);
      }
    } catch (error) {
      console.error('Error recalculating aggregates:', error);
      setValidationError('Failed to recalculate aggregates. Please try again.');
    } finally {
      setIsRecalculating(false);
    }
  };

  // Build files list from draft data (plFiles, tax1065Files, bankStatementFiles)
  // This is the primary source - these are the actual files associated with this case
  const getFilesFromDraftData = (): any[] => {
    const files: any[] = [];
    
    // P&L files
    const plFiles = data?.plFiles || [];
    plFiles.forEach((f: any, idx: number) => {
      files.push({
        upload_id: `pl-${idx}`,
        filename: f.filename || f.name || `P&L Document ${idx + 1}`,
        original_filename: f.filename || f.name || `P&L Document ${idx + 1}`,
        file_type: 'pdf',
        status: 'parsed',
        extraction_method: f.extraction_method || 'ai_extraction',
        transactions_count: f.transactions?.length || f.data?.transactions?.length || 0,
        source: 'draft_data',
        doc_type: 'pl',
      });
    });
    
    // Tax 1065 files
    const tax1065Files = data?.tax1065Files || [];
    tax1065Files.forEach((f: any, idx: number) => {
      files.push({
        upload_id: `tax-${idx}`,
        filename: f.filename || f.name || `Form 1065 (${f.year || 'Unknown Year'})`,
        original_filename: f.filename || f.name || `Form 1065 (${f.year || 'Unknown Year'})`,
        file_type: 'pdf',
        status: 'parsed',
        extraction_method: f.extraction_method || 'tax_parser',
        transactions_count: 0, // Tax forms don't have transaction counts
        source: 'draft_data',
        doc_type: 'tax1065',
        year: f.year,
        form_type: f.form_type,
      });
    });
    
    // Bank statement files
    const bankFiles = data?.bankStatementFiles || [];
    bankFiles.forEach((f: any, idx: number) => {
      files.push({
        upload_id: `bank-${idx}`,
        filename: f.filename || f.name || `Bank Statement ${idx + 1}`,
        original_filename: f.filename || f.name || `Bank Statement ${idx + 1}`,
        file_type: f.file_type || 'pdf',
        status: 'parsed',
        extraction_method: f.extraction_method || 'bank_parser',
        transactions_count: f.transactions?.length || 0,
        source: 'draft_data',
        doc_type: 'bank_statement',
      });
    });
    
    return files;
  };

  // Fetch uploaded files - use draft data first, then fall back to uploaded_files collection
  const fetchUploadedFiles = async () => {
    setFilesLoading(true);
    
    try {
      // Get case ID for querying
      const caseId = claimId;
      
      // PRIORITY 1: Check uploaded_files collection (S3-backed files with proper linking)
      if (caseId) {
        // Query by both claim_id and draft_id since either could be set
        const response = await api.get(`/api/v1/upload-savefirst/reprocess/available?claim_id=${caseId}&draft_id=${caseId}&limit=500`);
        if (response?.files?.length > 0) {
          setS3Files(response.files);
          console.log(`📁 Loaded ${response.files.length} files from uploaded_files collection`);
          return;
        }
      }
      
      // PRIORITY 2: Get files from draft data (plFiles, tax1065Files, bankStatementFiles)
      // These are legacy files stored inline in the draft, not in S3
      const draftFiles = getFilesFromDraftData();
      if (draftFiles.length > 0) {
        setS3Files(draftFiles);
        console.log(`📁 Loaded ${draftFiles.length} files from draft data (legacy)`);
        return;
      }

      // PRIORITY 3: Get files from unified_upload_data.file_results
      // These are wizard-uploaded files stored in the draft's unified upload structure
      // Read from prop (data) first since local state may have been cleared during sync
      const fileResults = data?.unified_upload_data?.file_results || unifiedUploadData?.file_results || [];
      if (fileResults.length > 0) {
        const mappedFiles = fileResults.map((fr: any, idx: number) => {
          const filename = fr.filename || fr.original_filename || `Document ${idx + 1}`;

          return {
            upload_id: fr._id || fr.id || `fr-${idx}`,
            filename,
            original_filename: fr.original_filename || fr.filename || filename,
            file_type: filename.split('.').pop()?.toLowerCase() || 'pdf',
            status: fr.status || 'parsed',
            extraction_method: fr.extraction_method || 'ai_extraction',
            transactions_count: fr.transactions_extracted || fr.transactions?.length || fr.data?.transactions?.length || 0,
            source: 'file_results',
            // Use backend-provided doc_type from DocumentRouter classification
            doc_type: fr.doc_type || fr.document_type || null,
            doc_category: fr.doc_category || null,
            classification_confidence: fr.classification_confidence || null,
            classification_method: fr.classification_method || null,
            s3_key: fr.s3_key || null,
            year: fr.year,
          };
        });
        setS3Files(mappedFiles);
        console.log(`📁 Loaded ${mappedFiles.length} files from unified_upload_data.file_results`);
        return;
      }

      // No files found in any source
      setS3Files([]);
      console.log('📁 No files found for this case');
    } catch (err) {
      console.error('Failed to fetch uploaded files:', err);
      setS3Files([]);
    } finally {
      setFilesLoading(false);
    }
  };


  // Open a processed file in the inline PDF viewer modal
  const handleViewFile = async (file: any) => {
    if (!file.s3_key) return;
    try {
      const response = await api.get(`/api/v1/reconciliation/source-pdf-url?s3_key=${encodeURIComponent(file.s3_key)}`);
      if (response.url) {
        setViewingFileUrl(response.url);
        setViewingFile(file);
      }
    } catch (err) {
      console.error('Failed to get view URL:', err);
    }
  };

  // Reprocess a single failed file
  const handleReprocessFile = async (uploadId: string) => {
    setReprocessingFileId(uploadId);
    try {
      const response = await api.post('/api/v1/upload-savefirst/reprocess', {
        upload_ids: [uploadId],
        limit: 1
      });
      
      if (response.results?.[0]?.status === 'success') {
        // Refresh the files list
        await fetchUploadedFiles();
      }
    } catch (err) {
      console.error('Reprocess failed:', err);
    } finally {
      setReprocessingFileId(null);
    }
  };

  // Load files when switching to files view OR on mount
  useEffect(() => {
    if (activeView === 'files' || s3Files.length === 0) {
      fetchUploadedFiles();
    }
  }, [activeView, claimId]);
  
  // Note: Removed duplicate fetchUploadedFiles() mount call — the useEffect above already runs on mount

  // Sync tax1065Files when data prop changes (e.g., after page refresh or upload)
  useEffect(() => {
    if (data.tax1065Files && data.tax1065Files.length > 0) {
      setTax1065Files(data.tax1065Files);
      console.log('📋 Synced tax1065Files from data:', data.tax1065Files.length, 'items');
    }
  }, [data.tax1065Files]);

  // NOTE: File classification, tax file extraction, and financial data building
  // are ALL handled by the backend during upload processing and canonical cache rebuild.
  // The useCanonicalData hook returns pre-built financial_data and tax1065Files.
  // No frontend reclassification or data processing needed.

  // Validate event date when financial data or event date changes
  useEffect(() => {
    const validateEventDate = async () => {
      // Only validate if we have both event date and financial data
      if (!eventStartDate || !data.financial_data) {
        setEventDateValidation(null);
        return;
      }
      
      // Check if we have year-keyed data
      const hasYearData = Object.keys(data.financial_data).some(k => k.startsWith('data_'));
      if (!hasYearData) {
        setEventDateValidation(null);
        return;
      }
      
      try {
        // Use centralized API utility
        const result = await api.post('/api/v1/reports/validate-event-date', {
          event_start_date: eventStartDate,
          financial_data: data.financial_data
        }, { showErrorToast: false });
        
        if (result.success) {
          setEventDateValidation(result.validation);
          console.log('📅 Event date validation:', result.validation);
        }
      } catch (error) {
        console.error('Event date validation error:', error);
      }
    };
    
    // Debounce validation
    const timeoutId = setTimeout(validateEventDate, 500);
    return () => clearTimeout(timeoutId);
  }, [eventStartDate, data.financial_data]);

  // Compute statistics from uploaded data OR from saved financial_data
  const stats = unifiedUploadData?.stats || {};
  const periods = hookPeriods.length > 0 ? hookPeriods : (unifiedUploadData?.canonical_data?.periods || []);
  
  // Also check aiExtractedData/financial_data for transaction counts
  const financialDataForStats = aiExtractedData || data.financial_data || {};
  
  // Debug logging
  console.log('📊 Stats computation:', {
    hasUnifiedUploadData: !!unifiedUploadData,
    hasAiExtractedData: !!aiExtractedData,
    aiExtractedDataKeys: Object.keys(aiExtractedData || {}),
    hasDataFinancialData: !!data.financial_data,
    dataFinancialDataKeys: Object.keys(data.financial_data || {}),
    financialDataForStatsKeys: Object.keys(financialDataForStats),
    isUploadMode,
  });
  
  const financialDataTransactionCount = Object.values(financialDataForStats)
    .filter((v): v is any[] => Array.isArray(v))
    .reduce((sum, yearData) => {
      return sum + yearData.reduce((ySum: number, month: any) => ySum + (month.transaction_count || 0), 0);
    }, 0);
  
  const financialDataPeriodCount = Object.values(financialDataForStats)
    .filter((v): v is any[] => Array.isArray(v))
    .reduce((sum, yearData) => sum + yearData.length, 0);
  
  // Calculate derived stats - prefer canonical data, fallback to financial_data
  const totalTransactions = stats.total_transactions || 
    periods.reduce((sum: number, p: any) => sum + (p.transactions?.length || 0), 0) ||
    financialDataTransactionCount;
  const totalPeriods = stats.total_periods || periods.length || financialDataPeriodCount;
  const totalFiles = stats.files_processed || 0;
  const categoriesCount = new Set(periods.flatMap((p: any) => (p.transactions || []).map((t: any) => t.category))).size;
  
  // Calculate items needing review
  const itemsNeedingReview = (() => {
    if (!periods.length) return stats.items_needing_review || 0;
    let needsReview = 0;
    // Expanded clear-cut categories that don't need manual review at high confidence
    const CLEAR_CUT = new Set([
      // Standard accounting categories
      'Utilities', 'utilities', 'UTILITIES',
      'Rent & Occupancy', 'Rent', 'rent', 'RENT',
      'Payroll & Benefits', 'Payroll', 'payroll', 'PAYROLL',
      'Insurance', 'insurance', 'INSURANCE',
      'Bank & Financial Fees', 'Bank Fees', 'BANK_FEES', 'bank_fees',
      'Sales Revenue', 'Revenue', 'revenue', 'REVENUE',
      'COGS', 'cogs', 'Cost of Goods Sold',
      'Inventory & Merchandise', 'inventory',
      'Food & Beverage Cost', 'food', 'FOOD',
      // Technology & software - common business expense
      'Technology & Software', 'technology & software', 'TECHNOLOGY',
      // Supplies & equipment
      'Supplies', 'supplies', 'SUPPLIES',
      'Equipment', 'equipment', 'EQUIPMENT',
      // Other common categories
      'Delivery', 'DELIVERY', 'delivery',
      'Marketing', 'MARKETING', 'marketing',
      'Professional Services', 'professional',
      'Taxes & Licenses', 'taxes',
      'Repairs & Maintenance', 'repairs',
    ]);
    for (const period of periods) {
      for (const txn of (period.transactions || [])) {
        const conf = txn.classification?.confidence || txn.confidence || 0;
        const cat = txn.category || '';
        const catLower = cat.toLowerCase();
        // Transaction needs review if:
        // 1. Category is uncategorized/unknown
        // 2. Confidence is low (<0.85) for any category
        // 3. Confidence is between 0.85-0.95 for non-clear-cut categories
        const isUncategorized = catLower === 'uncategorized' || catLower === '' || catLower === 'unknown';
        const isAutoApproved = !isUncategorized && (conf >= 0.95 || (CLEAR_CUT.has(cat) && conf >= 0.85));
        if (!isAutoApproved) needsReview++;
      }
    }
    return needsReview;
  })();

  // Get anomaly count
  const anomalyCount = cachedAnomalyAnalysis?.anomalies?.length || 0;

  // Date range calculation
  const dateRange = (() => {
    if (!periods.length) return null;
    const sorted = [...periods].sort((a, b) => {
      const dateA = new Date(a.year, (a.month || 1) - 1);
      const dateB = new Date(b.year, (b.month || 1) - 1);
      return dateA.getTime() - dateB.getTime();
    });
    const first = sorted[0];
    const last = sorted[sorted.length - 1];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[(first.month || 1) - 1]} ${first.year} - ${months[(last.month || 1) - 1]} ${last.year}`;
  })();

  // Handler for event date changes - ALSO persist to database
  // Memoized to prevent EventCoveragePeriod useEffect from re-firing on every render
  const handleEventDateChange = useCallback((field: 'event_start_date' | 'event_end_date' | 'fema_declaration_number', value: string) => {
    if (field === 'event_start_date') {
      setEventStartDate(value);
      setStartDatePrefilled(false);
      if (onDataUpdate) onDataUpdate({ event_start_date: value });
    } else if (field === 'event_end_date') {
      setEventEndDate(value);
      if (onDataUpdate) onDataUpdate({ event_end_date: value });
    } else if (field === 'fema_declaration_number') {
      setFemaDeclarationNumber(value);
      if (onDataUpdate) onDataUpdate({ fema_declaration_number: value });
    }
  }, [onDataUpdate]); // eslint-disable-line

  // Handler for anomaly resolution
  const handleResolveAnomaly = (anomalyId: string, note: string) => {
    const newResolved = [...resolvedAnomalies, { 
      id: anomalyId, 
      note, 
      resolved_at: new Date().toISOString() 
    }];
    setResolvedAnomalies(newResolved);
    if (onDataUpdate) {
      onDataUpdate({ resolved_anomalies: newResolved });
    }
  };

  const handleUnresolveAnomaly = (anomalyId: string) => {
    const newResolved = resolvedAnomalies.filter(r => r.id !== anomalyId);
    setResolvedAnomalies(newResolved);
    if (onDataUpdate) {
      onDataUpdate({ resolved_anomalies: newResolved });
    }
  };

  const handleAnalysisComplete = (analysis: { anomalies: any[]; dataHash: string; analyzedAt: string }) => {
    setCachedAnomalyAnalysis(analysis);
    if (onDataUpdate) {
      onDataUpdate({ cached_anomaly_analysis: analysis });
    }
  };

  // Handle forensic insight fetch - cache to database
  const handleForensicInsightFetched = (anomalyId: string, insight: any) => {
    // Update local state
    setCachedAnomalyAnalysis((prev: any) => {
      if (!prev || !prev.anomalies) return prev;
      
      const updatedAnomalies = prev.anomalies.map((a: any) => {
        if (a.id === anomalyId) {
          return {
            ...a,
            forensic_insight: insight.forensic_insight,
            forensic_analysis: insight,
            forensic_analyzed_at: new Date().toISOString()
          };
        }
        return a;
      });
      
      const updated = { ...prev, anomalies: updatedAnomalies };
      
      // Persist to database
      if (onDataUpdate) {
        onDataUpdate({ cached_anomaly_analysis: updated });
      }
      
      return updated;
    });
  };

  // Handle upload complete from UnifiedUploader
  const handleUploadComplete = async (result: any) => {
    console.log('📦 UnifiedUploader complete:', result);
    console.log('📊 Stats:', result.stats);
    console.log('📄 File results:', result.file_results?.length || 0, 'files');
    console.log('📋 tax1065Files from result:', result.tax1065Files?.length || 0, 'files');
    
    // Track that upload has happened
    setUnifiedUploadData(result);
    
    // ✅ CRITICAL FIX: MERGE tax1065Files from the job result with existing files
    // Don't replace - merge new files with existing ones to support batch uploads
    let mergedTaxFiles = tax1065Files; // Start with existing
    if (result.tax1065Files && result.tax1065Files.length > 0) {
      console.log('📝 Merging tax1065Files from job result');
      console.log('📝 New files:', result.tax1065Files.length, 'Existing files:', tax1065Files.length);
      
      // Merge: keep existing files, add/update from result
      const existingMap = new Map(tax1065Files.map((f: any) => [f.filename, f]));
      result.tax1065Files.forEach((newFile: any) => {
        existingMap.set(newFile.filename, newFile);
      });
      mergedTaxFiles = Array.from(existingMap.values());
      console.log('📝 Merged total:', mergedTaxFiles.length);
      setTax1065Files(mergedTaxFiles);
    }
    
    if (result.canonical_data && result.canonical_data.periods?.length > 0) {
      const yearlyData: Record<string, any[]> = {};
      const years: number[] = [];
      
      // Convert periods to data_YYYY format
      result.canonical_data.periods?.forEach((period: any) => {
        const year = period.year;
        const month = period.month || 1;
        
        if (!years.includes(year)) years.push(year);
        
        const yearKey = `data_${year}`;
        if (!yearlyData[yearKey]) yearlyData[yearKey] = [];
        
        yearlyData[yearKey].push({
          year,
          month,
          revenue: period.gross_revenue || 0,
          cogs: period.cogs || 0,
          payroll: (period.employee_wages || 0) + (period.officer_compensation || 0),
          employee_wages: period.employee_wages || 0,
          officer_compensation: period.officer_compensation || 0,
          fixed_costs: period.fixed_costs || 0,
          variable_costs: period.variable_costs || 0,
          net_profit: period.net_profit || 0,
          line_items: period.line_items || {},
          transaction_count: period.transactions?.length || 0,
        });
      });
      
      years.sort((a, b) => a - b);
      
      // Sort monthly data within each year
      Object.keys(yearlyData).forEach(yearKey => {
        yearlyData[yearKey].sort((a: any, b: any) => a.month - b.month);
      });
      
      const financialData: Record<string, any> = { ...yearlyData };
      
      // Update state
      setUnifiedUploadData(result);
      setAiExtractedData(financialData);
      if (result.tax1065Files?.length > 0) {
        setTax1065Files(result.tax1065Files);
      }
      
      // Build payroll data for Step 5 compatibility
      const payrollData: Record<string, any> = {};
      years.forEach(year => {
        const yearData = yearlyData[`data_${year}`] || [];
        const totalPayroll = yearData.reduce((sum: number, m: any) => sum + (m.payroll || 0), 0);
        const totalEmployeeWages = yearData.reduce((sum: number, m: any) => sum + (m.employee_wages || 0), 0);
        const totalOfficerComp = yearData.reduce((sum: number, m: any) => sum + (m.officer_compensation || 0), 0);
        
        payrollData[year] = {
          total: totalPayroll,
          employee_wages: totalEmployeeWages,
          officer_compensation: totalOfficerComp,
        };
      });
      
      // Update wizard data
      if (onDataUpdate) {
        const updatedData: Record<string, any> = {
          ...data,
          financial_data: financialData,
          fromUnifiedUpload: true,
          unified_upload_data: result,
          calculated_years: years,
          payroll_data: payrollData,
          tax1065Files: mergedTaxFiles,  // Use merged files, not just result
        };
        
        // Add year-specific payroll fields
        years.forEach(year => {
          updatedData[`payroll_${year}`] = payrollData[year]?.total || 0;
          updatedData[`employee_payroll_${year}`] = payrollData[year]?.employee_wages || 0;
          updatedData[`officer_compensation_${year}`] = payrollData[year]?.officer_compensation || 0;
        });
        
        const fullDataToSave = { ...data, ...updatedData };
        onDataUpdate(updatedData);
        
        // Trigger save
        if (onSave) {
          setTimeout(async () => {
            try {
              await onSave(fullDataToSave);
              console.log('✅ Save completed');
              // SSoT: Refetch canonical data from ClaimService for authoritative source_file/source_page metadata
              refetchCanonical();
            } catch (err) {
              console.error('❌ Save failed:', err);
            }
          }, 100);
        }
      }
    } else {
      // Even without canonical_data, store file_results for display
      console.log('⚠️ No canonical_data in result, but upload complete');
      if (result.file_results?.length > 0) {
        console.log('📄 File results available:', result.file_results.length);
      }
      
      // ✅ CRITICAL: Still update wizard data with tax files even without canonical_data
      // Tax documents don't produce canonical_data, but we still need to save their extracted data
      if (onDataUpdate && mergedTaxFiles.length > 0) {
        console.log('📝 Saving tax1065Files without canonical_data, count:', mergedTaxFiles.length);
        const updatedData: Record<string, any> = {
          ...data,
          fromUnifiedUpload: true,
          unified_upload_data: result,
          tax1065Files: mergedTaxFiles,  // Use merged files
        };
        
        const fullDataToSave = { ...data, ...updatedData };
        onDataUpdate(updatedData);
        
        // Trigger save with the full data including tax files with extracted data
        if (onSave) {
          setTimeout(async () => {
            try {
              await onSave(fullDataToSave);
              console.log('✅ Save completed (tax files only)');
              refetchCanonical();
            } catch (err) {
              console.error('❌ Save failed:', err);
            }
          }, 100);
        }
      }
    }
    
    // Always switch to review mode after upload completes
    setIsUploadMode(false);
    setActiveView('summary');
  };

  // Handle navigation to next step
  const handleNext = () => {
    const years = Object.keys(aiExtractedData || {})
      .filter(key => key.startsWith('data_'))
      .map(key => parseInt(key.replace('data_', '')))
      .filter(y => !isNaN(y))
      .sort((a, b) => a - b);
    
    onNext({
      ...data,
      financial_data: aiExtractedData,
      fromUnifiedUpload: true,
      unified_upload_data: unifiedUploadData,
      calculated_years: years,
      tax1065Files: tax1065Files,
      event_start_date: persistableEventStartDate(eventStartDate, startDatePrefilled, data),
      event_end_date: eventEndDate,
      fema_declaration_number: femaDeclarationNumber,
    });
  };

  // Auto-advance to next step when all data is ready (100% AI automation)
  // NO HUMAN CLICKS REQUIRED - if data is ready, advance automatically
  // Don't auto-advance if data was already loaded from a saved draft (user is reviewing)
  // Only auto-advance after a FRESH upload in this session
  const dataAlreadyLoaded = !!(data.financial_data && Object.keys(data.financial_data).some(k => k.startsWith('data_')));
  const isNavigatingBack = data.auto_advanced === true || data.step > 3;
  const [hasAutoAdvanced, setHasAutoAdvanced] = useState(dataAlreadyLoaded || isNavigatingBack);

  // Mutable refs for the values the auto-advance effect needs to READ at
  // fire-time but that should NOT retrigger the effect on every change.
  // Previously the effect depended on `data`, `onNext`, `onDataUpdate`,
  // `unifiedUploadData`, `tax1065Files`, `femaDeclarationNumber`,
  // `cachedAnomalyAnalysis` — every one of those gets new identity on
  // virtually every render, so the effect re-fired constantly (which
  // cancelled and rebuilt the 3s timer, re-ran auto-detection, and
  // contributed to the render cascade + scroll reset behavior).
  const autoAdvanceRefs = useRef({
    data,
    onNext,
    onDataUpdate,
    aiExtractedData,
    unifiedUploadData,
    tax1065Files,
    femaDeclarationNumber,
    cachedAnomalyAnalysis,
    eventStartDate,
    eventEndDate,
    startDatePrefilled,
  });
  autoAdvanceRefs.current = {
    data,
    onNext,
    onDataUpdate,
    aiExtractedData,
    unifiedUploadData,
    tax1065Files,
    femaDeclarationNumber,
    cachedAnomalyAnalysis,
    eventStartDate,
    eventEndDate,
    startDatePrefilled,
  };

  // Boolean presence signals — these are the only things that should
  // retrigger the auto-advance effect.  Computed here so they have stable
  // primitive identity across renders when the underlying data is unchanged.
  const hasAutoAdvanceAiData = !!aiExtractedData && Object.keys(aiExtractedData || {}).length > 0;
  const hasAutoAdvanceAnomalies = !!cachedAnomalyAnalysis?.analyzedAt;
  const hasAutoAdvanceStart = !!eventStartDate;
  const hasAutoAdvanceEnd = !!eventEndDate;

  useEffect(() => {
    // Skip if already auto-advanced or navigating back
    if (hasAutoAdvanced) return;

    // Skip if in upload mode or method selection (user is still choosing)
    const { aiExtractedData: ai, cachedAnomalyAnalysis: anom, eventStartDate: es, eventEndDate: ee } = autoAdvanceRefs.current;
    if (isUploadMode && !ai) return;
    if (shouldShowMethodSelection) return;

    // Check if we have all required data for auto-advance
    const hasData = ai && Object.keys(ai).length > 0;
    const hasAnalyzedAnomalies = anom?.analyzedAt;
    
    // For event dates: use what we have or auto-detect from anomalies
    let effectiveStartDate = es;
    let effectiveEndDate = ee;
    let anomalyStart: string | undefined;

    if (!effectiveStartDate || !effectiveEndDate) {
      // Try to detect from anomalies
      const anomalies = anom?.anomalies || [];
      const revenueDrops = anomalies.filter((a: any) =>
        a.anomaly_type === 'revenue_drop' &&
        a.affected_period &&
        !a.affected_period.endsWith('-01') &&
        Math.abs(a.deviation_percent || 0) > 40
      );

      if (revenueDrops.length > 0) {
        const biggestDrop = revenueDrops.reduce((max: any, curr: any) =>
          Math.abs(curr.deviation_percent || 0) > Math.abs(max.deviation_percent || 0) ? curr : max
        );
        const [year, month] = biggestDrop.affected_period.split('-').map(Number);
        anomalyStart = `${year}-${String(month).padStart(2, '0')}-01`;
        if (!effectiveEndDate) effectiveEndDate = `${year + 1}-${String(month).padStart(2, '0')}-01`;
      }
    }

    // user value > confirmed EI date > anomaly default; only the anomaly default is persisted
    const startPick = pickAutoEventStart({
      es,
      confirmed: getConfirmedEventDate(autoAdvanceRefs.current.data),
      anomalyStart,
    });
    effectiveStartDate = startPick.value;

    const hasEventDates = effectiveStartDate && effectiveEndDate;

    // AUTO-ADVANCE CONDITIONS:
    // 1. Has financial data (from uploads)
    // 2. Anomalies have been analyzed by AI
    // 3. Event dates are available (set or auto-detected)
    if (hasData && hasAnalyzedAnomalies && hasEventDates) {
      console.log('🚀 AUTO-ADVANCING: All data ready, delaying 3s so user can see summary');
      console.log(`   - Financial data: ✓`);
      console.log(`   - Anomalies analyzed: ✓`);
      console.log(`   - Event dates: ${effectiveStartDate} to ${effectiveEndDate}`);

      setHasAutoAdvanced(true);

      // Update event dates if they were auto-detected (batched)
      const dateUpdates: Record<string, string> = {};
      if (!es && effectiveStartDate) {
        setEventStartDate(effectiveStartDate);
        if (startPick.persist) {
          dateUpdates.event_start_date = effectiveStartDate;
        } else {
          setStartDatePrefilled(true);
        }
      }
      if (!ee && effectiveEndDate) {
        setEventEndDate(effectiveEndDate);
        dateUpdates.event_end_date = effectiveEndDate;
      }
      if (autoAdvanceRefs.current.onDataUpdate && Object.keys(dateUpdates).length > 0) {
        autoAdvanceRefs.current.onDataUpdate(dateUpdates);
      }

      // Build the data payload and advance — READ latest values at fire-time
      // from the ref so we don't need them as effect deps.
      const timer = setTimeout(() => {
        const latest = autoAdvanceRefs.current;
        const years = Object.keys(latest.aiExtractedData || {})
          .filter(key => key.startsWith('data_'))
          .map(key => parseInt(key.replace('data_', '')))
          .filter(y => !isNaN(y))
          .sort((a, b) => a - b);

        latest.onNext({
          ...latest.data,
          financial_data: latest.aiExtractedData,
          fromUnifiedUpload: true,
          unified_upload_data: latest.unifiedUploadData,
          calculated_years: years,
          tax1065Files: latest.tax1065Files,
          event_start_date: persistableEventStartDate(
            effectiveStartDate || '',
            latest.startDatePrefilled || startPick.source === 'confirmed',
            latest.data,
          ),
          event_end_date: effectiveEndDate,
          fema_declaration_number: latest.femaDeclarationNumber,
          auto_advanced: true, // Flag to indicate this was automated
        });
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [
    // Intentionally narrow dep array.  We only want the effect to re-evaluate
    // the GATE conditions (hasAutoAdvanced flag, mode flags, and the arrived-
    // vs-not-arrived booleans derived from ai/anom/event dates).  All data
    // used inside the effect body is read from `autoAdvanceRefs.current` so
    // it can't retrigger the effect just because the data reference changed.
    hasAutoAdvanced,
    isUploadMode,
    shouldShowMethodSelection,
    hasAutoAdvanceAiData,
    hasAutoAdvanceAnomalies,
    hasAutoAdvanceStart,
    hasAutoAdvanceEnd,
  ]);

  // Local flags only — used for QA + button-disabled state. Completion
  // percent and letter grade are intentionally NOT computed here; the
  // sidebar renders only what the backend supplies. SSoT contract per
  // docs/plans/2026-04-25-frontend-calc-drift-audit.md (Fix B).
  const hasFinancialData = aiExtractedData && Object.keys(aiExtractedData).length > 0;
  const hasTaxFiles = tax1065Files.length > 0;

  // Build sidebar sections based on mode
  const buildSections = () => {
    // Count uploaded files from S3 state
    const uploadedFilesCount = s3Files.length || uploadedDocuments?.length || data?.uploadedDocuments?.length || 0;
    
    if (isUploadMode) {
      return [
        { 
          id: 'upload', 
          label: 'Upload Files', 
          isActive: activeView === 'upload',
          isComplete: false,
          onClick: () => setActiveView('upload')
        },
        { 
          id: 'files', 
          label: `Uploaded Files (${uploadedFilesCount})`, 
          isActive: activeView === 'files',
          isComplete: uploadedFilesCount > 0,
          onClick: () => setActiveView('files')
        },
        { 
          id: 'review', 
          label: 'Review Data', 
          isActive: false,
          isComplete: false,
          isLocked: true,
          onClick: () => {}
        },
      ];
    }
    
    return [
      { 
        id: 'upload', 
        label: 'Upload More', 
        isActive: activeView === 'upload',
        isComplete: true,
        onClick: () => setActiveView('upload')
      },
      { 
        id: 'files', 
        label: `Uploaded Files (${uploadedFilesCount})`, 
        isActive: activeView === 'files',
        isComplete: uploadedFilesCount > 0,
        onClick: () => setActiveView('files')
      },
      { 
        id: 'summary', 
        label: 'Summary', 
        isActive: activeView === 'summary',
        isComplete: hasFinancialData,
        onClick: () => setActiveView('summary')
      },
      { 
        id: 'monthly', 
        label: `Monthly P&L (${totalPeriods})`, 
        isActive: activeView === 'monthly',
        isComplete: totalPeriods > 0,
        onClick: () => setActiveView('monthly')
      },
      { 
        id: 'transactions', 
        label: `Transactions (${totalTransactions.toLocaleString()})`, 
        isActive: activeView === 'transactions',
        isComplete: totalTransactions > 0,
        onClick: () => setActiveView('transactions')
      },
      { 
        id: 'categories', 
        label: `Categories (${categoriesCount})`, 
        isActive: activeView === 'categories',
        isComplete: categoriesCount > 0,
        onClick: () => setActiveView('categories')
      },
      {
        id: 'tax',
        label: hasTaxFiles ? `Tax Docs (${tax1065Files.length})` : 'Tax Docs',
        isActive: activeView === 'tax',
        isComplete: hasTaxFiles,
        onClick: () => setActiveView('tax')
      },
      // Revenue Sources — third-party merchant/processor settlement statements.
      // Sibling to Tax Docs, not nested under it: different forensic role
      // (revenue source evidence vs. tax validation).  Appears whenever any
      // merchant statements have been uploaded OR user has picked a
      // processor / sales tax state in the card — so the forensic accountant
      // can always get back to the card they filled in.
      ...((() => {
        const hasMerchantStatements = (data.revenue_source_statements || []).length > 0;
        const rsData = data.revenue_sources || {};
        const hasMerchantMetadata =
          (rsData.processors || []).some((p: any) => p.applies) ||
          !!(rsData.sales_tax && rsData.sales_tax.state);
        if (!hasMerchantStatements && !hasMerchantMetadata) return [];
        const stmtCount = (data.revenue_source_statements || []).length;
        return [{
          id: 'revenue_sources',
          label: stmtCount > 0 ? `Revenue Sources (${stmtCount})` : 'Revenue Sources',
          isActive: activeView === 'revenue_sources',
          isComplete: !!(data.revenue_source_reconciliation),
          onClick: () => setActiveView('revenue_sources'),
        }];
      })()),
      {
        id: 'reconciliation',
        label: 'Reconciliation',
        isActive: activeView === 'reconciliation',
        isComplete: hasTaxFiles && hasFinancialData,
        onClick: () => setActiveView('reconciliation')
      },
      { 
        id: 'anomalies', 
        label: `Anomalies (${anomalyCount})`, 
        isActive: activeView === 'anomalies',
        isComplete: anomalyCount === 0,
        onClick: () => setActiveView('anomalies')
      },
    ];
  };

  // Build QA items for sidebar
  const buildQAItems = (): QAItem[] => {
    const items: QAItem[] = [];
    
    if (!hasFinancialData) {
      items.push({ id: 'data', label: 'Financial Data', status: 'missing' });
    }
    
    if (itemsNeedingReview > 0) {
      items.push({ id: 'review', label: `${pluralize(itemsNeedingReview, 'item')} to review`, status: 'warning' });
    }
    
    if (anomalyCount > 0) {
      items.push({ id: 'anomalies', label: `${anomalyCount} anomalies`, status: 'warning' });
    }
    
    return items;
  };

  // Render Files View - Show all uploaded S3 files with download/reprocess options
  const renderFilesView = () => {
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
    
    // Group files by status
    const parsedFiles = s3Files.filter(f => f.status === 'parsed');
    const failedFiles = s3Files.filter(f => f.status === 'failed');
    const processingFiles = s3Files.filter(f => f.status === 'processing' || f.status === 'uploaded');
    
    return (
      <div className="p-6 h-full overflow-y-auto">
        <div className="mb-6">
          <h2 className="cs-h2">Uploaded Files</h2>
          <p className="cs-meta mt-1">
            Files uploaded to S3 for this case. Click to download or retry failed extractions.
          </p>
        </div>

        {filesLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--color-brand)' }} />
            <span className="ml-2 cs-meta">Loading files...</span>
          </div>
        ) : s3Files.length === 0 ? (
          <div className="text-center py-12">
            <Folder className="w-16 h-16 mx-auto mb-4" style={{ color: 'var(--color-text-dim)' }} />
            <p className="cs-meta">No files uploaded yet</p>
            <button
              onClick={() => setActiveView('upload')}
              className="mt-4 px-4 py-2 rounded-lg"
              style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
            >
              Upload Files
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Summary */}
            <div className="flex gap-4 text-sm">
              <span className="cs-chip px-3 py-1 rounded-full" style={{ background: 'transparent', color: 'var(--color-verified)' }}>
                {parsedFiles.length} Processed
              </span>
              {failedFiles.length > 0 && (
                <span className="cs-chip px-3 py-1 rounded-full" style={{ background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>
                  {failedFiles.length} Failed
                </span>
              )}
              {processingFiles.length > 0 && (
                <span className="cs-chip px-3 py-1 rounded-full" style={{ background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>
                  {processingFiles.length} Processing
                </span>
              )}
            </div>

            {/* Failed Files Section */}
            {failedFiles.length > 0 && (
              <div className="rounded-lg p-4" style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}>
                <h3 className="cs-h3 mb-3 flex items-center gap-2" style={{ color: 'var(--color-needs-human-text)' }}>
                  <AlertTriangle className="w-5 h-5" />
                  Failed Extractions ({failedFiles.length})
                </h3>
                <div className="space-y-2">
                  {failedFiles.map((file) => (
                    <div key={file.upload_id || file._id} className="flex items-center justify-between p-3 rounded" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
                      <div className="flex items-center gap-3">
                        <FileWarning className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />
                        <div>
                          <p className="cs-body" style={{ fontWeight: 500 }}>{file.original_filename || file.filename}</p>
                          <p className="cs-meta" style={{ color: 'var(--color-needs-human-text)' }}>{file.error || 'Processing failed'}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleReprocessFile(file.upload_id || file._id)}
                        disabled={reprocessingFileId === (file.upload_id || file._id)}
                        className="flex items-center gap-1 px-3 py-1.5 text-sm rounded disabled:opacity-50"
                        style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
                      >
                        {reprocessingFileId === (file.upload_id || file._id) ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <RefreshCw className="w-4 h-4" />
                        )}
                        Retry
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Processed Files */}
            <div>
              <h3 className="cs-h3 mb-3 flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
                <CheckCircle className="w-5 h-5" style={{ color: 'var(--color-verified)' }} />
                Successfully Processed ({parsedFiles.length})
              </h3>
              <div className="grid gap-2">
                {parsedFiles.map((file) => (
                  <div key={file.upload_id || file._id} className="flex items-center justify-between p-3 rounded" style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)' }}>
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <FileText className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--color-verified)' }} />
                      <div className="min-w-0">
                        {file.s3_key ? (
                          <button
                            onClick={() => handleViewFile(file)}
                            className="cs-body hover:underline cursor-pointer truncate block max-w-full text-left"
                            style={{ color: 'var(--color-needs-human-text)', fontWeight: 500 }}
                            title={`View ${file.original_filename || file.filename}`}
                          >
                            {file.original_filename || file.filename}
                          </button>
                        ) : (
                          <p className="cs-body truncate" style={{ fontWeight: 500 }}>{file.original_filename || file.filename}</p>
                        )}
                        <p className="cs-meta">
                          {(() => {
                            const dt = (file.doc_type || '').toLowerCase();
                            const fn = (file.original_filename || file.filename || '').toLowerCase();
                            const em = (file.extraction_method || '').toLowerCase();
                            const combined = `${dt} ${fn} ${em}`;
                            const txnCount = file.transactions_count || file.parsed_data?.transactions?.length || 0;

                            // Tax documents
                            const isTax = combined.includes('tax') || combined.includes('k-1') || combined.includes('k1')
                              || combined.includes('1065') || combined.includes('1120') || combined.includes('schedule_c');
                            if (isTax) return <>Tax Document {file.year && `(${file.year})`}</>;

                            // Insurance / FNOL / claims / declarations
                            const isInsurance = combined.includes('insurance') || combined.includes('insurance_policy')
                              || combined.includes('fnol') || combined.includes('declaration')
                              || combined.includes('endorsement') || combined.includes('binder')
                              || combined.includes('coverage') || combined.includes('certificate_of');
                            if (isInsurance) return <>Insurance Document</>;

                            // Fire / police / government reports
                            const isReport = combined.includes('fire') || combined.includes('_fd')
                              || combined.includes('police') || combined.includes('incident_report')
                              || combined.includes('arson') || combined.includes('investigation');
                            if (isReport) return <>Case Report</>;

                            // Manual entry with no transactions
                            const isManual = em === 'manual_entry';
                            if (isManual && txnCount === 0) return <>Manual Entry</>;

                            // Files with actual transactions
                            if (txnCount > 0) return <>{txnCount} transaction{txnCount !== 1 ? 's' : ''}</>;

                            // Unclassified with no transactions — show as supporting document
                            return <>Supporting Document</>;
                          })()}
                          {file.extraction_method && file.extraction_method !== 'unknown' && ` · ${file.extraction_method}`}
                          {file.source === 'draft_data' && ' · From wizard upload'}
                        </p>
                      </div>
                    </div>
                    {file.s3_key ? (
                      <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                        <button
                          onClick={() => handleViewFile(file)}
                          className="p-1.5 rounded transition-colors"
                          style={{ color: 'var(--color-needs-human-text)' }}
                          title="View document"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={async () => {
                            try {
                              const response = await api.get(`/api/v1/reconciliation/source-pdf-url?s3_key=${encodeURIComponent(file.s3_key)}`);
                              if (response.url) {
                                window.open(response.url, '_blank');
                              }
                            } catch (err) {
                              console.error('Failed to get download URL:', err);
                            }
                          }}
                          className="p-1.5 rounded transition-colors"
                          style={{ color: 'var(--color-text-muted)' }}
                          title="Download"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <span className="cs-meta px-2 py-1 rounded flex-shrink-0" style={{ background: 'var(--color-bg-elevated)' }}>
                        Processed
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
            
            {/* Processing Files */}
            {processingFiles.length > 0 && (
              <div>
                <h3 className="cs-h3 mb-3 flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
                  <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--color-brand)' }} />
                  Processing ({processingFiles.length})
                </h3>
                <div className="grid gap-2">
                  {processingFiles.map((file) => (
                    <div key={file.upload_id || file._id} className="flex items-center gap-3 p-3 rounded" style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}>
                      <FileText className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />
                      <p className="cs-body">{file.original_filename || file.filename}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Refresh Button */}
            <div className="flex items-center gap-4 pt-4" style={{ borderTop: '1px solid var(--color-border)' }}>
              <button
                onClick={fetchUploadedFiles}
                className="flex items-center gap-2 text-sm"
                style={{ color: 'var(--color-needs-human-text)' }}
              >
                <RefreshCw className="w-4 h-4" />
                Refresh List
              </button>
              
              {failedFiles.length > 0 && (
                <button
                  onClick={async () => {
                    setReprocessingFileId('all-failed');
                    try {
                      const response = await api.post('/api/v1/upload-savefirst/reprocess/failed', {
                        limit: 100
                      });
                      console.log('Reprocess result:', response);
                      await fetchUploadedFiles();
                    } catch (err) {
                      console.error('Reprocess failed:', err);
                    } finally {
                      setReprocessingFileId(null);
                    }
                  }}
                  disabled={reprocessingFileId === 'all-failed'}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm rounded disabled:opacity-50"
                  style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
                >
                  {reprocessingFileId === 'all-failed' ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4" />
                  )}
                  Reprocess All Failed ({failedFiles.length})
                </button>
              )}
              
              <button
                onClick={async () => {
                  setReprocessingFileId('all');
                  try {
                    // Reprocess all files with status 'processing' or 'uploaded' 
                    const response = await api.post('/api/v1/upload-savefirst/reprocess', {
                      status_filter: 'processing',
                      limit: 100
                    });
                    console.log('Reprocess result:', response);
                    await fetchUploadedFiles();
                  } catch (err) {
                    console.error('Reprocess failed:', err);
                  } finally {
                    setReprocessingFileId(null);
                  }
                }}
                disabled={reprocessingFileId === 'all'}
                className="flex items-center gap-2 px-3 py-1.5 text-sm rounded disabled:opacity-50"
                style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
              >
                {reprocessingFileId === 'all' ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
                Reprocess Stuck Files
              </button>
            </div>
          </div>
        )}

        {/* Inline PDF Viewer Modal */}
        {viewingFile && (
          <PDFViewerModal
            isOpen={!!viewingFile}
            reportId={claimId}
            sourceFile={viewingFile.original_filename || viewingFile.filename}
            sourcePage={1}
            pdfUrlOverride={viewingFileUrl || undefined}
            onClose={() => { setViewingFile(null); setViewingFileUrl(null); }}
          />
        )}
      </div>
    );
  };

  // Render Upload Mode content - Full width without nested boxes
  const renderUploadMode = () => (
    <div className="h-full flex flex-col">
      {/* Main upload area - fills available space */}
      <div className="flex-1 overflow-y-auto">
        <UnifiedUploader
          onComplete={handleUploadComplete}
          onBack={() => setEntryMethod('select')}
          wizardData={data}
          compact={true}
          reportId={claimId}
        />
      </div>
    </div>
  );

  // Render Method Selection screen (Quick Start)
  const renderMethodSelection = () => (
    <div className="p-8">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <h2 className="cs-h2 mb-2">How would you like to enter financial data?</h2>
          <p className="cs-body">Choose the method that best fits your situation</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Upload Documents Option */}
          <button
            onClick={() => {
              setEntryMethod('upload');
              setIsUploadMode(true);
              setActiveView('upload');
            }}
            className="rounded-xl shadow-lg p-6 hover:shadow-xl transition-all border-2 text-left group"
            style={{ background: 'var(--color-bg-surface)', borderColor: 'transparent' }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--color-brand)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'transparent'; }}
          >
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 rounded-xl flex items-center justify-center transition" style={{ background: 'var(--color-needs-human-tint)' }}>
                <Upload className="w-7 h-7" style={{ color: 'var(--color-brand)' }} />
              </div>
              <div>
                <h3 className="cs-h3" style={{ color: 'var(--color-text-primary)' }}>Upload Documents</h3>
                <p className="cs-meta" style={{ color: 'var(--color-needs-human-text)', fontWeight: 500 }}>Most accurate</p>
              </div>
            </div>
            <p className="cs-body mb-4">
              Upload bank statements, P&L reports, tax returns, or QuickBooks exports. Our AI extracts and categorizes your financial data automatically.
            </p>
            <div className="flex flex-wrap gap-2">
              <span className="cs-chip px-2 py-1 rounded" style={{ background: 'var(--color-bg-elevated)', color: 'var(--color-text-muted)' }}>PDF</span>
              <span className="cs-chip px-2 py-1 rounded" style={{ background: 'var(--color-bg-elevated)', color: 'var(--color-text-muted)' }}>Excel</span>
              <span className="cs-chip px-2 py-1 rounded" style={{ background: 'var(--color-bg-elevated)', color: 'var(--color-text-muted)' }}>CSV</span>
              <span className="cs-chip px-2 py-1 rounded" style={{ background: 'var(--color-bg-elevated)', color: 'var(--color-text-muted)' }}>Tax Forms</span>
            </div>
          </button>

          {/* Industry Templates Option */}
          <button
            onClick={() => {
              setEntryMethod('template');
              setActiveView('template');
            }}
            className="rounded-xl shadow-lg p-6 hover:shadow-xl transition-all border-2 text-left group"
            style={{ background: 'var(--color-bg-surface)', borderColor: 'transparent' }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--color-brand)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'transparent'; }}
          >
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 rounded-xl flex items-center justify-center transition" style={{ background: 'var(--color-needs-human-tint)' }}>
                <LayoutTemplate className="w-7 h-7" style={{ color: 'var(--color-brand)' }} />
              </div>
              <div>
                <h3 className="cs-h3" style={{ color: 'var(--color-text-primary)' }}>Industry Templates</h3>
                <p className="cs-meta" style={{ color: 'var(--color-needs-human-text)', fontWeight: 500 }}>Quick start • No documents</p>
              </div>
            </div>
            <p className="cs-body mb-4">
              Don't have documents ready? Select your industry type and enter revenue estimates. We'll generate P&L data using verified industry benchmarks.
            </p>
            <div className="flex flex-wrap gap-2">
              <span className="cs-chip px-2 py-1 rounded" style={{ background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>Restaurant</span>
              <span className="cs-chip px-2 py-1 rounded" style={{ background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>Retail</span>
              <span className="cs-chip px-2 py-1 rounded" style={{ background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>Service</span>
              <span className="cs-chip px-2 py-1 rounded" style={{ background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>+10 more</span>
            </div>
          </button>
        </div>

        {/* Back button */}
        <div className="mt-8 text-center">
          <button
            onClick={onBack}
            className="font-semibold"
            style={{ color: 'var(--color-text-secondary)' }}
          >
            ← Back to Step 2
          </button>
        </div>
      </div>
    </div>
  );

  // Render Template Selection view
  const renderTemplateView = () => (
    <div className="p-6">
      <TemplateSelector
        businessType={data.business_type}
        currentRevenue={data.annual_revenue || 0}
        coverageYear={resolveCoverageYear({ ...data, event_start_date: eventStartDate }) ?? -1}
        onApplyTemplate={(templateData) => {
          console.log('✅ Template applied:', templateData);
          
          // Transform template financial_data format to wizard format
          // Template returns: { data_YYYY: { revenue: [...], cogs: [...], payroll: [...], expenses: [...] } }
          // Wizard expects: { data_YYYY: [{ revenue: X, cogs: Y, payroll: Z, expenses: W }, ...] }
          const transformedFinancialData: Record<string, any[]> = {};
          
          if (templateData.financial_data) {
            Object.entries(templateData.financial_data).forEach(([key, yearData]: [string, any]) => {
              if (key.startsWith('data_') && yearData) {
                // Check if data needs transformation (array format vs object format)
                if (yearData.revenue && Array.isArray(yearData.revenue)) {
                  // Transform from parallel arrays to array of objects
                  const months = yearData.revenue.length;
                  const monthlyData = [];
                  
                  for (let i = 0; i < months; i++) {
                    monthlyData.push({
                      revenue: yearData.revenue[i] || 0,
                      cogs: yearData.cogs?.[i] || 0,
                      payroll: yearData.payroll?.[i] || 0,
                      expenses: yearData.expenses?.[i] || 0,
                      month: i + 1
                    });
                  }
                  
                  transformedFinancialData[key] = monthlyData;
                } else if (Array.isArray(yearData)) {
                  // Already in correct format
                  transformedFinancialData[key] = yearData;
                }
              }
            });
          }
          
          const updatedTemplateData = {
            ...templateData,
            financial_data: transformedFinancialData
          };
          
          setAppliedTemplate(updatedTemplateData);
          
          // Update wizard data with template data
          if (onDataUpdate) {
            onDataUpdate({
              financial_data: transformedFinancialData,
              reconstructed_pl: templateData.reconstructed_pl,
              fromTemplate: true,
              applied_template: updatedTemplateData,
              template_id: templateData.template_id,
              template_name: templateData.template_name
            });
          }
          
          // Also update local state
          setAiExtractedData(transformedFinancialData);
          
          // Switch to template_complete view
          setActiveView('template_complete');
          setIsUploadMode(false);
          setEntryMethod('upload'); // Set to upload so hasExistingData logic works
        }}
      />
      
      {/* Back button */}
      <div className="mt-6 text-center">
        <button
          onClick={() => setEntryMethod('select')}
          className="font-semibold"
          style={{ color: 'var(--color-text-secondary)' }}
        >
          ← Change Method
        </button>
      </div>
    </div>
  );

  // Render Template Complete view
  const renderTemplateComplete = () => {
    const template = appliedTemplate || data.applied_template;
    
    return (
      <div className="p-6 space-y-6">
        {/* Success Header */}
        <div className="flex items-center gap-4 p-4 rounded-xl" style={{ background: 'transparent', border: '1px solid var(--color-verified)' }}>
          <div className="w-14 h-14 rounded-xl flex items-center justify-center" style={{ background: 'transparent' }}>
            <span className="text-3xl">{template?.template_icon || '📊'}</span>
          </div>
          <div className="flex-1">
            <p className="cs-body font-medium flex items-center gap-2" style={{ color: 'var(--color-verified)' }}>
              <CheckCircle className="w-5 h-5" style={{ color: 'var(--color-verified)' }} />
              {template?.template_name || 'Industry Template'} applied successfully
            </p>
            <p className="cs-meta mt-1" style={{ color: 'var(--color-verified)' }}>
              P&L estimates generated based on industry benchmarks
            </p>
          </div>
          <button
            onClick={() => {
              setEntryMethod('template');
              setActiveView('template');
            }}
            className="px-4 py-2 rounded-lg"
            style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-verified)', color: 'var(--color-verified)' }}
          >
            Change Template
          </button>
        </div>

        {/* Generated Data Summary */}
        {template?.financial_data && (
          <div className="rounded-xl p-6" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
            <h3 className="cs-h3 mb-4" style={{ color: 'var(--color-text-primary)' }}>Generated Financial Data</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(template.financial_data)
                .filter(([key]) => key.startsWith('data_'))
                .map(([key, yearData]: [string, any]) => {
                  const year = key.replace('data_', '');
                  let totalRevenue = 0;
                  
                  if (Array.isArray(yearData)) {
                    // Transformed format: [{revenue: X, ...}, ...]
                    totalRevenue = yearData.reduce((sum: number, m: any) => sum + (m.revenue || 0), 0);
                  } else if (yearData?.revenue && Array.isArray(yearData.revenue)) {
                    // Original template format: {revenue: [...], ...}
                    totalRevenue = yearData.revenue.reduce((sum: number, val: number) => sum + val, 0);
                  }
                  
                  return (
                    <div key={key} className="rounded-lg p-4 text-center" style={{ background: 'var(--color-bg-elevated)' }}>
                      <div className="cs-figure-lg">{year}</div>
                      <div className="cs-figure">
                        ${totalRevenue.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        )}

        {/* Industry Benchmarks */}
        {template?.benchmarks && (
          <div className="rounded-xl p-6" style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}>
            <h3 className="cs-h3 mb-4 flex items-center gap-2" style={{ color: 'var(--color-needs-human-text)' }}>
              <BarChart3 className="w-5 h-5" />
              Industry Benchmarks Applied
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div className="rounded-lg p-3 text-center" style={{ background: 'var(--color-bg-surface)' }}>
                <div className="cs-figure" style={{ color: 'var(--color-needs-human-text)', fontWeight: 700 }}>
                  {template.benchmarks.gross_margin_avg || template.defaults_used?.cogs_pct ? (100 - (template.defaults_used?.cogs_pct || 0)) : 0}%
                </div>
                <div className="cs-meta">Gross Margin</div>
              </div>
              <div className="rounded-lg p-3 text-center" style={{ background: 'var(--color-bg-surface)' }}>
                <div className="cs-figure" style={{ color: 'var(--color-needs-human-text)', fontWeight: 700 }}>
                  {template.benchmarks.net_margin_avg || 0}%
                </div>
                <div className="cs-meta">Net Margin</div>
              </div>
              <div className="rounded-lg p-3 text-center" style={{ background: 'var(--color-bg-surface)' }}>
                <div className="cs-figure" style={{ color: 'var(--color-needs-human-text)', fontWeight: 700 }}>
                  {template.defaults_used?.payroll_pct || 0}%
                </div>
                <div className="cs-meta">Labor Cost</div>
              </div>
              <div className="rounded-lg p-3 text-center" style={{ background: 'var(--color-bg-surface)' }}>
                <div className="cs-figure" style={{ color: 'var(--color-needs-human-text)', fontWeight: 700 }}>
                  ${(template.defaults_used?.rent_monthly || 0).toLocaleString()}
                </div>
                <div className="cs-meta">Monthly Rent</div>
              </div>
            </div>
          </div>
        )}

        {/* Event Coverage Period — EventCoveragePeriod self-cards + self-titles (EventCoveragePeriod.tsx:237-244).
            B3 dedup: dropped the redundant outer card + "Event Coverage Period" heading that nested a
            second identical title inside the DS acquisition seam. Required-ness is enforced by the
            disabled Continue CTA + validationError, not a heading tag. */}
        <div>
          <EventCoveragePeriod
            financialData={template?.financial_data || data.financial_data}
            plFiles={[]}
            eventStartDate={eventStartDate}
            eventEndDate={eventEndDate}
            femaDeclarationNumber={femaDeclarationNumber}
            onDateChange={handleEventDateChange}
            businessAddress={data.business_address}
            eventIntelligence={data.event_intelligence}
          />
          
          {/* Event Date Validation Warning */}
          {eventDateValidation && !eventDateValidation.is_valid && (
            <div className="mt-4 rounded-lg p-4" style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}>
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--color-brand)' }} />
                <div className="flex-1">
                  <h4 className="cs-body font-medium" style={{ color: 'var(--color-needs-human-text)' }}>Event Date May Need Adjustment</h4>
                  <ul className="mt-1 cs-meta space-y-1" style={{ color: 'var(--color-needs-human-text)' }}>
                    {eventDateValidation.warnings.map((warning, idx) => (
                      <li key={idx}>• {warning}</li>
                    ))}
                  </ul>
                  {eventDateValidation.suggestions.length > 0 && (
                    <div className="mt-2 cs-meta" style={{ color: 'var(--color-needs-human-text)' }}>
                      <strong>Suggestion:</strong> {eventDateValidation.suggestions[0]}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Industry Tips */}
        {template?.tips && template.tips.length > 0 && (
          <div className="rounded-xl p-6" style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}>
            <h3 className="cs-h3 mb-3 flex items-center gap-2" style={{ color: 'var(--color-needs-human-text)' }}>
              <Sparkles className="w-5 h-5" />
              Industry Tips for {template.template_name}
            </h3>
            <ul className="space-y-2 cs-body" style={{ color: 'var(--color-needs-human-text)' }}>
              {template.tips.map((tip: string, idx: number) => (
                <li key={idx} className="flex items-start gap-2">
                  <span className="mt-1" style={{ color: 'var(--color-brand)' }}>•</span>
                  <span>{tip}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  // Render Summary View content
  const renderSummaryView = () => (
    <div className="p-6 lg:p-8">
      <div className="space-y-6">
        {/* Document Requirements Checklist - Hard Gate for Accountant Queue */}
        {claimId && (
          <DocumentChecklist 
            caseId={claimId}
            onReadinessChange={(ready) => {
              console.log('📋 Document readiness changed:', ready);
            }}
          />
        )}
        
        {/* Upload Summary Stats */}
        <div className="rounded-xl p-6" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
          <h2 className="cs-h3 mb-4 flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
            <CheckCircle className="w-5 h-5" style={{ color: 'var(--color-verified)' }} />
            Import Summary
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            {/* K3 §7.7: stat values are neutral DM Mono, not orange — orange budget on this
                stage is the active stage underline + the one forward CTA only. */}
            <div className="rounded-lg p-4 text-center" style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border-sub)' }}>
              <div className="cs-figure-lg" style={{ color: 'var(--color-text-primary)' }}>{totalTransactions.toLocaleString()}</div>
              <div className="cs-meta">Transactions</div>
            </div>
            <div className="rounded-lg p-4 text-center" style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border-sub)' }}>
              <div className="cs-figure-lg" style={{ color: 'var(--color-text-primary)' }}>{totalPeriods}</div>
              <div className="cs-meta">Months of Data</div>
            </div>
            <div className="rounded-lg p-4 text-center" style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border-sub)' }}>
              <div className="cs-figure-lg" style={{ color: 'var(--color-text-primary)' }}>{categoriesCount}</div>
              <div className="cs-meta">Categories</div>
            </div>
            {/* Labeled "Needs Classification Review" (not bare "Needs Review") so it is
                never read as arguing with the "No Anomalies Detected" forensic scan — this
                tile counts uncategorized / low-confidence CLASSIFICATIONS, a different pile.
                The full label is the fix; a subtitle on this compact tile wraps and gets
                skimmed wrong (owner, 2026-09-06). */}
            <div className="rounded-lg p-4 text-center" style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border-sub)' }}>
              <div className="cs-figure-lg" style={{ color: 'var(--color-text-primary)' }}>{itemsNeedingReview}</div>
              <div className="cs-meta">Needs Classification Review</div>
            </div>
          </div>

          {dateRange && (
            <p className="cs-meta">
              <Calendar className="w-4 h-4 inline mr-1" />
              Data covers: <strong>{dateRange}</strong>
            </p>
          )}
          
          {/* Action buttons */}
          <div className="flex gap-3 mt-4">
            <button
              onClick={() => {
                setIsUploadMode(true);
                setActiveView('upload');
              }}
              className="flex items-center gap-2 px-4 py-2 rounded-lg transition-colors text-sm font-medium"
              style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)' }}
            >
              <Plus className="w-4 h-4" />
              Upload More Files
            </button>
            <button
              onClick={() => {
                // Reset and re-upload
                setUnifiedUploadData(null);
                setAiExtractedData(null);
                setIsUploadMode(true);
                setActiveView('upload');
              }}
              className="flex items-center gap-2 px-4 py-2 rounded-lg transition-colors text-sm font-medium"
              style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)' }}
            >
              <RefreshCw className="w-4 h-4" />
              Re-process
            </button>
            <button
              onClick={handleRecalculateAggregates}
              disabled={isRecalculating}
              className="flex items-center gap-2 px-4 py-2 rounded-lg transition-colors text-sm font-medium disabled:opacity-50"
              style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)' }}
              title="Recalculate P&L totals from transaction categories"
            >
              {isRecalculating ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <BarChart3 className="w-4 h-4" />
              )}
              {isRecalculating ? 'Recalculating...' : 'Recalculate P&L'}
            </button>
            <button
              onClick={handleDiagnose}
              disabled={isDiagnosing}
              className="flex items-center gap-2 px-4 py-2 rounded-lg transition-colors text-sm font-medium disabled:opacity-50"
              style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)' }}
              title="Diagnose data issues and find missing values"
            >
              {isDiagnosing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <AlertTriangle className="w-4 h-4" />
              )}
              {isDiagnosing ? 'Diagnosing...' : 'Diagnose Issues'}
            </button>
          </div>
        </div>

        {/* Diagnostics Modal */}
        {showDiagnostics && diagnosticResults && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden" style={{ background: 'var(--color-bg-surface)' }}>
              <div className="px-6 py-4 flex items-center justify-between" style={{ borderBottom: '1px solid var(--color-border)' }}>
                <h3 className="cs-h3 flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
                  <AlertTriangle className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />
                  Data Diagnostics Report
                </h3>
                <button
                  onClick={() => setShowDiagnostics(false)}
                  className="transition-colors"
                  style={{ color: 'var(--color-text-muted)' }}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 overflow-y-auto max-h-[60vh]">
                {/* Summary */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="rounded-lg p-4 text-center" style={{ background: 'var(--color-bg-elevated)' }}>
                    <div className="cs-figure-lg">{diagnosticResults.total_periods}</div>
                    <div className="cs-meta">Periods</div>
                  </div>
                  <div className="rounded-lg p-4 text-center" style={{ background: 'var(--color-bg-elevated)' }}>
                    <div className="cs-figure-lg">{diagnosticResults.total_transactions}</div>
                    <div className="cs-meta">Transactions</div>
                  </div>
                  <div className="rounded-lg p-4 text-center" style={{ background: diagnosticResults.issues?.length > 0 ? 'var(--color-needs-human-tint)' : 'transparent' }}>
                    <div className="cs-figure-lg" style={{ color: diagnosticResults.issues?.length > 0 ? 'var(--color-needs-human-text)' : 'var(--color-verified)' }}>
                      {diagnosticResults.issues?.length || 0}
                    </div>
                    <div className="cs-meta">Issues Found</div>
                  </div>
                </div>
                
                {/* Issues List */}
                {diagnosticResults.issues?.length > 0 && (
                  <div className="mb-6">
                    <h4 className="cs-body font-medium mb-3" style={{ color: 'var(--color-text-primary)' }}>Issues Detected</h4>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {diagnosticResults.issues.slice(0, 10).map((issue: any, idx: number) => (
                        <div
                          key={idx}
                          className="p-3 rounded-lg"
                          style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}
                        >
                          <div className="flex items-center gap-2">
                            <span className="cs-chip px-2 py-0.5 rounded" style={{ background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>
                              {issue.severity?.toUpperCase()}
                            </span>
                            <span className="cs-body font-medium" style={{ color: 'var(--color-text-secondary)' }}>{issue.period}</span>
                          </div>
                          <p className="cs-meta mt-1">{issue.message}</p>
                          {issue.potential_revenue && (
                            <p className="cs-meta mt-1" style={{ color: 'var(--color-verified)' }}>
                              Potential revenue: ${issue.potential_revenue.toLocaleString()}
                            </p>
                          )}
                          {issue.potential_cogs && (
                            <p className="cs-meta mt-1" style={{ color: 'var(--color-needs-human-text)' }}>
                              Potential COGS: ${issue.potential_cogs.toLocaleString()}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                    {diagnosticResults.issues.length > 10 && (
                      <p className="cs-meta mt-2">
                        And {diagnosticResults.issues.length - 10} more issues...
                      </p>
                    )}
                  </div>
                )}

                {/* Recommendations */}
                <div className="mb-6">
                  <h4 className="cs-body font-medium mb-3" style={{ color: 'var(--color-text-primary)' }}>Recommendations</h4>
                  <div className="space-y-2">
                    {diagnosticResults.recommendations?.map((rec: string, idx: number) => (
                      <div key={idx} className="flex items-start gap-2 cs-body">
                        <span>{rec}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Summary Stats */}
                {diagnosticResults.summary && (
                  <div className="rounded-lg p-4" style={{ background: 'var(--color-bg-elevated)' }}>
                    <h4 className="cs-body font-medium mb-2" style={{ color: 'var(--color-text-primary)' }}>Current Totals</h4>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>Revenue: <span className="font-medium" style={{ color: 'var(--color-verified)' }}>${diagnosticResults.summary.total_revenue?.toLocaleString() || 0}</span></div>
                      <div>COGS: <span className="font-medium" style={{ color: 'var(--color-needs-human-text)' }}>${diagnosticResults.summary.total_cogs?.toLocaleString() || 0}</span></div>
                      <div>Uncategorized: <span className="font-medium">{diagnosticResults.summary.uncategorized_percentage || 0}%</span></div>
                      <div>Periods with issues: <span className="font-medium">{diagnosticResults.summary.periods_with_issues || 0}</span></div>
                    </div>
                  </div>
                )}
              </div>

              <div className="px-6 py-4 flex justify-end gap-3" style={{ borderTop: '1px solid var(--color-border)' }}>
                <button
                  onClick={() => setShowDiagnostics(false)}
                  className="px-4 py-2 rounded-lg transition-colors"
                  style={{ color: 'var(--color-text-secondary)' }}
                >
                  Close
                </button>
                {diagnosticResults.issues?.length > 0 && (
                  <button
                    onClick={handleFixIssues}
                    disabled={isRecalculating}
                    className="px-4 py-2 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
                    style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
                  >
                    {isRecalculating ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Fixing...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-4 h-4" />
                        Fix All Issues
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Financial Data Table */}
        {aiExtractedData && Object.keys(aiExtractedData).length > 0 && (
          <div className="rounded-xl overflow-hidden" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
            <div className="px-6 py-4" style={{ background: 'var(--color-bg-elevated)', borderBottom: '1px solid var(--color-border)' }}>
              <h3 className="cs-h3" style={{ color: 'var(--color-text-primary)' }}>Financial Data Summary</h3>
            </div>
            {/* B3: cap the 48-row monthly table (~560px ≈ 13 rows) so it stops dominating the
                stage; the thead sticks to the container top while rows scroll under it. */}
            <div className="overflow-auto max-h-[560px]">
              <table className="w-full text-sm">
                <thead className="sticky top-0 z-10" style={{ background: 'var(--color-bg-elevated)' }}>
                  <tr>
                    <th className="text-left py-3 px-4 cs-label">Period</th>
                    <th className="text-right py-3 px-4 cs-label">Revenue</th>
                    <th className="text-right py-3 px-4 cs-label">Payroll</th>
                    <th className="text-right py-3 px-4 cs-label">Fixed</th>
                    <th className="text-right py-3 px-4 cs-label">Variable</th>
                    <th className="text-right py-3 px-4 cs-label">COGS</th>
                    <th className="text-right py-3 px-4 cs-label">Txns</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(aiExtractedData)
                    .filter(([key]) => key.startsWith('data_'))
                    .sort(([a], [b]) => a.localeCompare(b))
                    .flatMap(([yearKey, months]: [string, any]) => {
                      // Extract year from key like "data_2021" -> 2021
                      const year = parseInt(yearKey.replace('data_', ''), 10);
                      return (Array.isArray(months) ? months : []).map((m: any, mi: number) => {
                        // K3 Bug 1 guard: a missing month must NOT render "YYYY-undefined"
                        // (String(undefined).padStart(2) === "undefined"). Fall back to the
                        // year alone, matching the merchant-recon table (L3297). Canonical
                        // data always carries month (1-12) so this is defensive only; `rowKey`
                        // stays unique so month-less rows don't collapse to one React key.
                        const my = m.year || year;
                        const hasMonth = m.month != null && !Number.isNaN(Number(m.month));
                        return {
                          ...m,
                          year: my,  // Use year from data or from key
                          periodKey: hasMonth ? `${my}-${String(m.month).padStart(2, '0')}` : `${my}`,
                          rowKey: hasMonth ? `${my}-${String(m.month).padStart(2, '0')}` : `${my}-row${mi}`,
                        };
                      });
                    })
                    .sort((a, b) => a.periodKey.localeCompare(b.periodKey))
                    .map((m: any) => (
                      <tr key={m.rowKey} style={{ borderBottom: '1px solid var(--color-border-faint)' }}>
                        <td className="py-3 px-4 cs-figure" style={{ color: 'var(--color-text-primary)', fontWeight: 500 }}>{m.periodKey}</td>
                        <td className="text-right px-4 cs-figure" style={{ color: 'var(--color-verified)', fontWeight: 500 }}>
                          ${(m.revenue || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}
                        </td>
                        <td className="text-right px-4 cs-figure">
                          ${(m.payroll || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}
                        </td>
                        <td className="text-right px-4 cs-figure">
                          ${(m.fixed_costs || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}
                        </td>
                        <td className="text-right px-4 cs-figure">
                          ${(m.variable_costs || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}
                        </td>
                        <td className="text-right px-4 cs-figure">
                          ${(m.cogs || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}
                        </td>
                        <td className="text-right px-4 cs-figure" style={{ color: 'var(--color-text-muted)' }}>{m.transaction_count || 0}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Anomaly Banner */}
        {unifiedUploadData?.canonical_data && (
          <AnomalyBanner
            canonicalData={unifiedUploadData.canonical_data}
            resolvedAnomalies={resolvedAnomalies}
            draftId={claimId}
            businessContext={{
              business_type: data.business_type,
              industry: data.naics_code,
              event_type: data.event_type,
              event_start: eventStartDate,
              event_end: eventEndDate
            }}
            enableAIAnalysis={true}
            cachedAnalysis={cachedAnomalyAnalysis}
            onAnalysisComplete={handleAnalysisComplete}
            onViewTransaction={(txnId) => {
              setHighlightedTransactionId(txnId);
              setActiveView('transactions');
            }}
            onResolveAnomaly={handleResolveAnomaly}
            onUnresolveAnomaly={handleUnresolveAnomaly}
          />
        )}

        {/* Event Coverage Period — self-carded component (B3 dedup, see the summary-view block). */}
        <div>
          <EventCoveragePeriod
            financialData={aiExtractedData || data.financial_data}
            plFiles={[]}
            eventStartDate={eventStartDate}
            eventEndDate={eventEndDate}
            femaDeclarationNumber={femaDeclarationNumber}
            onDateChange={handleEventDateChange}
            businessAddress={data.business_address}
            eventIntelligence={data.event_intelligence}
          />
        </div>
      </div>
    </div>
  );

  // Render Monthly P&L View
  const renderMonthlyView = () => (
    <div className="p-6 lg:p-8">
      <div className="rounded-xl overflow-hidden" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
        <div className="px-6 py-4" style={{ background: 'var(--color-bg-elevated)', borderBottom: '1px solid var(--color-border)' }}>
          <h3 className="cs-h3" style={{ color: 'var(--color-text-primary)' }}>Monthly P&L Data</h3>
          <p className="cs-meta mt-1">Detailed monthly breakdown of financial data</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead style={{ background: 'var(--color-bg-elevated)' }}>
              <tr>
                <th className="text-left py-3 px-4 cs-label">Period</th>
                <th className="text-right py-3 px-4 cs-label">Revenue</th>
                <th className="text-right py-3 px-4 cs-label">COGS</th>
                <th className="text-right py-3 px-4 cs-label">Gross Profit</th>
                <th className="text-right py-3 px-4 cs-label">Payroll</th>
                <th className="text-right py-3 px-4 cs-label">Fixed</th>
                <th className="text-right py-3 px-4 cs-label">Variable</th>
                <th className="text-right py-3 px-4 cs-label">Net Profit</th>
              </tr>
            </thead>
            <tbody>
              {aiExtractedData && Object.entries(aiExtractedData)
                .filter(([key]) => key.startsWith('data_'))
                .sort(([a], [b]) => a.localeCompare(b))
                .flatMap(([yearKey, months]: [string, any]) => {
                  // Extract year from key like "data_2021" -> 2021
                  const year = parseInt(yearKey.replace('data_', ''), 10);
                  return (Array.isArray(months) ? months : []).map((m: any, mi: number) => {
                    // K3 Bug 1 guard (see the summary table above): missing month must not
                    // render "YYYY-undefined"; fall back to the year, keep the React key unique.
                    const my = m.year || year;
                    const hasMonth = m.month != null && !Number.isNaN(Number(m.month));
                    return {
                      ...m,
                      year: my,
                      periodKey: hasMonth ? `${my}-${String(m.month).padStart(2, '0')}` : `${my}`,
                      rowKey: hasMonth ? `${my}-${String(m.month).padStart(2, '0')}` : `${my}-row${mi}`,
                      grossProfit: (m.revenue || 0) - (m.cogs || 0)
                    };
                  });
                })
                .sort((a, b) => a.periodKey.localeCompare(b.periodKey))
                .map((m: any) => (
                  <tr key={m.rowKey} style={{ borderBottom: '1px solid var(--color-border-faint)' }}>
                    <td className="py-3 px-4 cs-figure" style={{ color: 'var(--color-text-primary)', fontWeight: 500 }}>{m.periodKey}</td>
                    <td className="text-right px-4 cs-figure" style={{ color: 'var(--color-verified)', fontWeight: 500 }}>
                      ${(m.revenue || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}
                    </td>
                    <td className="text-right px-4 cs-figure">
                      ${(m.cogs || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}
                    </td>
                    <td className="text-right px-4 cs-figure" style={{ color: 'var(--color-needs-human-text)', fontWeight: 500 }}>
                      ${m.grossProfit.toLocaleString(undefined, {maximumFractionDigits: 0})}
                    </td>
                    <td className="text-right px-4 cs-figure">
                      ${(m.payroll || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}
                    </td>
                    <td className="text-right px-4 cs-figure">
                      ${(m.fixed_costs || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}
                    </td>
                    <td className="text-right px-4 cs-figure">
                      ${(m.variable_costs || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}
                    </td>
                    {/* Canonical monthly field is `net_income` (verified: all 48 rows on
                        d6d88aea carry net_income, none carry net_profit → the old m.net_profit
                        read rendered $0 on every row). `?? net_profit` keeps any legacy
                        template-entry source that still emits net_profit working. */}
                    <td className="text-right px-4 cs-figure font-medium" style={{ color: ((m.net_income ?? m.net_profit) || 0) >= 0 ? 'var(--color-verified)' : 'var(--color-text-muted)' }}>
                      ${((m.net_income ?? m.net_profit) || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  // Render Transactions View
  const renderTransactionsView = () => (
    <div className="p-6 lg:p-8">
      {/* Client Clarification Panel */}
      {flaggedTransactions.length > 0 && (
        <div className="mb-6 rounded-xl overflow-hidden" style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}>
          <button
            onClick={() => setShowClarificationPanel(!showClarificationPanel)}
            className="w-full p-4 flex items-center justify-between transition"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg" style={{ background: 'var(--color-needs-human-tint)' }}>
                <MessageSquare className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />
              </div>
              <div className="text-left">
                <h3 className="cs-h3" style={{ color: 'var(--color-needs-human-text)' }}>
                  {flaggedTransactions.length} Transaction{flaggedTransactions.length > 1 ? 's' : ''} Flagged for Client
                </h3>
                <p className="cs-meta" style={{ color: 'var(--color-needs-human-text)' }}>
                  {clarificationSent ? 'Clarification request sent' : 'Ready to send clarification request'}
                </p>
              </div>
            </div>
            <ChevronDown className={`w-5 h-5 transition-transform ${showClarificationPanel ? 'rotate-180' : ''}`} style={{ color: 'var(--color-brand)' }} />
          </button>

          {showClarificationPanel && (
            <div className="p-4" style={{ borderTop: '1px solid var(--color-needs-human-tint-bd)', background: 'var(--color-bg-surface)' }}>
              {/* Flagged transactions list */}
              <div className="space-y-2 mb-4 max-h-48 overflow-y-auto">
                {flaggedTransactions.map((txn, idx) => (
                  <div key={txn.id} className="flex items-start gap-3 p-3 rounded-lg" style={{ background: 'var(--color-bg-elevated)' }}>
                    <span className="flex-shrink-0 w-6 h-6 cs-chip rounded-full flex items-center justify-center" style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}>
                      {idx + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="cs-body font-medium truncate" style={{ color: 'var(--color-text-primary)' }}>{txn.description}</p>
                      <p className="cs-meta">{txn.date} • ${Math.abs(txn.amount).toLocaleString()}</p>
                      <p className="cs-meta mt-1 italic" style={{ color: 'var(--color-needs-human-text)' }}>"{txn.question}"</p>
                    </div>
                    <button
                      onClick={() => {
                        setFlaggedTransactions(prev => prev.filter(f => f.id !== txn.id));
                      }}
                      className="p-1 transition"
                      style={{ color: 'var(--color-text-muted)' }}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>

              {/* Email input */}
              <div className="mb-4">
                <label className="block cs-body font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>Client Email</label>
                <input
                  type="email"
                  value={clientEmail}
                  onChange={(e) => setClientEmail(e.target.value)}
                  placeholder="client@example.com"
                  className="w-full px-3 py-2 rounded-lg text-sm focus:ring-2"
                  style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
                />
              </div>
              
              {/* Send button */}
              <div className="flex items-center gap-3">
                <button
                  onClick={async () => {
                    console.log('Send button clicked');
                    console.log('Client email:', clientEmail);
                    console.log('Flagged transactions:', flaggedTransactions);
                    
                    if (!clientEmail || flaggedTransactions.length === 0) {
                      console.log('Validation failed - missing email or no flagged transactions');
                      setValidationError('Please enter a client email and flag at least one transaction');
                      return;
                    }
                    
                    setSendingClarification(true);
                    try {
                      const apiUrl = process.env.REACT_APP_BACKEND_URL;
                      console.log('API URL:', apiUrl);
                      
                      // Get the correct report/draft ID - check multiple sources
                      const reportId = claimId;
                      console.log('Report ID:', reportId);
                      console.log('Data keys:', Object.keys(data));
                      
                      if (!reportId) {
                        setValidationError('No draft ID found. Please save your work first.');
                        return;
                      }
                      
                      // Create clarification request
                      const createPayload = {
                        report_id: reportId,
                        claimant_email: clientEmail,
                        transactions: flaggedTransactions.map(t => ({
                          transaction_id: t.id,
                          date: t.date,
                          description: t.description,
                          amount: t.amount,
                          current_category: t.category,
                          question: t.question,
                          options: ['Cost of Goods Sold', 'Office Supplies', 'Professional Services', 'Equipment', 'Other']
                        }))
                      };
                      console.log('Create payload:', createPayload);
                      
                      // Use centralized API utility
                      const createData = await api.post('/api/v1/clarifications/create', createPayload, { showErrorToast: false });
                      console.log('Create response data:', createData);
                      const { request_id } = createData;
                      
                      // Send the email
                      console.log('Sending email for request_id:', request_id);
                      const sendData = await api.post(`/api/v1/clarifications/send/${request_id}`, {}, { showErrorToast: false });
                      console.log('Send response data:', sendData);
                      
                      setClarificationSent(true);
                      setStatusMessage('Clarification request sent successfully!');
                      
                      // Save to data
                      onDataUpdate?.({
                        ...data,
                        flagged_transactions: flaggedTransactions,
                        last_clarification_sent: new Date().toISOString(),
                        clarification_request_id: request_id
                      });
                    } catch (err: any) {
                      console.error('Failed to send clarification:', err);
                      setValidationError(`Error: ${err.message || 'Unknown error occurred'}`);
                    } finally {
                      setSendingClarification(false);
                    }
                  }}
                  disabled={sendingClarification || !clientEmail || clarificationSent}
                  className="flex-1 py-2.5 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition"
                  style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
                >
                  {sendingClarification ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Sending...
                    </>
                  ) : clarificationSent ? (
                    <>
                      <CheckCircle2 className="w-4 h-4" />
                      Sent Successfully
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Send Clarification Request
                    </>
                  )}
                </button>
                {itemsNeedingReview > flaggedTransactions.length && (
                  <p className="cs-meta flex items-center gap-1" style={{ color: 'var(--color-needs-human-text)' }}>
                    <AlertTriangle className="w-3.5 h-3.5" /> {itemsNeedingReview - flaggedTransactions.length} transactions still unreviewed
                  </p>
                )}
              </div>

              {/* Preview note */}
              <p className="cs-meta mt-3">
                This will send: {flaggedTransactions.length} transaction question{flaggedTransactions.length > 1 ? 's' : ''} to {clientEmail || 'client'}
              </p>
            </div>
          )}
        </div>
      )}
      
      {/* Bank / Credit Card filter toggle */}
      {unifiedUploadData?.canonical_data?.periods && (
        <div className="flex items-center gap-2 mb-4">
          {(['all', 'bank', 'credit_card'] as const).map((filter) => {
            const labels = { all: 'All Transactions', bank: 'Bank Statements', credit_card: 'Credit Cards' };
            const isActive = txnSourceFilter === filter;
            return (
              <button
                key={filter}
                onClick={() => setTxnSourceFilter(filter)}
                className="px-4 py-2 rounded-lg text-sm font-medium transition"
                style={isActive
                  ? { background: 'var(--color-bg-hover)', color: 'var(--color-text-primary)', border: '1px solid var(--color-border-strong)' }
                  : { background: 'var(--color-bg-elevated)', color: 'var(--color-text-muted)', border: '1px solid transparent' }}
              >
                {labels[filter]}
              </button>
            );
          })}
        </div>
      )}

      {(() => { console.log('🔴 TXN_VIEWER_GATE:', { hasUnifiedUploadData: !!unifiedUploadData, hasCanonicalData: !!unifiedUploadData?.canonical_data, periodsCount: unifiedUploadData?.canonical_data?.periods?.length, hookPeriodsCount: hookPeriods?.length, txnSourceFilter, firstPeriodTxns: unifiedUploadData?.canonical_data?.periods?.[0]?.transactions?.length }); return null; })()}
      {unifiedUploadData?.canonical_data?.periods ? (
        <TransactionViewer
          periods={
            txnSourceFilter === 'all'
              ? unifiedUploadData.canonical_data.periods
              : unifiedUploadData.canonical_data.periods.map((p: any) => ({
                  ...p,
                  transactions: (p.transactions || []).filter((t: any) =>
                    txnSourceFilter === 'bank'
                      ? t.source_type !== 'credit_card_statement'
                      : t.source_type === 'credit_card_statement'
                  ),
                }))
          }
          reportId={claimId}
          highlightedTransactionId={highlightedTransactionId}
          onHighlightClear={() => setHighlightedTransactionId(null)}
          flaggedTransactions={flaggedTransactions}
          onFlagForClient={(txn) => {
            setFlaggedTransactions(prev => {
              // Don't add if already flagged
              if (prev.some(f => f.id === txn.id)) return prev;
              return [...prev, txn];
            });
            // Auto-expand panel when first item is flagged
            if (flaggedTransactions.length === 0) {
              setShowClarificationPanel(true);
            }
          }}
          onUnflagTransaction={(txnId) => {
            setFlaggedTransactions(prev => prev.filter(f => f.id !== txnId));
          }}
          onTransactionEdit={(periodKey, txnIndex, updates) => {
            setUnifiedUploadData((prev: any) => {
              if (!prev?.canonical_data?.periods) return prev;
              
              const newPeriods = prev.canonical_data.periods.map((period: any) => {
                const pKey = `${period.year}-${String(period.month).padStart(2, '0')}`;
                if (pKey !== periodKey) return period;
                
                const newTransactions = [...(period.transactions || [])];
                if (newTransactions[txnIndex]) {
                  newTransactions[txnIndex] = {
                    ...newTransactions[txnIndex],
                    ...updates,
                    needs_review: updates.needs_review ?? newTransactions[txnIndex].needs_review,
                  };
                }
                
                return { ...period, transactions: newTransactions };
              });
              
              return {
                ...prev,
                canonical_data: {
                  ...prev.canonical_data,
                  periods: newPeriods,
                },
              };
            });
          }}
          onBulkApprove={(transactionIds) => {
            setUnifiedUploadData((prev: any) => {
              if (!prev?.canonical_data?.periods) return prev;
              
              const idSet = new Set(transactionIds);
              
              const newPeriods = prev.canonical_data.periods.map((period: any) => {
                const newTransactions = (period.transactions || []).map((txn: any, idx: number) => {
                  const txnId = `${period.year}-${String(period.month).padStart(2, '0')}-${idx}`;
                  if (idSet.has(txnId)) {
                    return {
                      ...txn,
                      validation_status: 'approved',
                      needs_review: false,
                    };
                  }
                  return txn;
                });
                
                return { ...period, transactions: newTransactions };
              });
              
              return {
                ...prev,
                canonical_data: {
                  ...prev.canonical_data,
                  periods: newPeriods,
                },
              };
            });
          }}
        />
      ) : (
        <div className="text-center py-12" style={{ color: 'var(--color-text-muted)' }}>
          <FileSpreadsheet className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--color-text-dim)' }} />
          <p>No transaction data available.</p>
          <p className="cs-meta mt-2">Upload financial documents to see transactions.</p>
        </div>
      )}
    </div>
  );

  // Render Categories View — expandable with transaction details
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  const renderCategoriesView = () => {
    // Group transactions by category with full transaction references
    const categoryData: Record<string, { count: number; amount: number; transactions: any[] }> = {};
    periods.forEach((period: any) => {
      (period.transactions || []).forEach((txn: any) => {
        const cat = txn.category || 'Uncategorized';
        if (!categoryData[cat]) {
          categoryData[cat] = { count: 0, amount: 0, transactions: [] };
        }
        categoryData[cat].count++;
        categoryData[cat].amount += Math.abs(txn.amount || 0);
        categoryData[cat].transactions.push({ ...txn, _period_year: period.year, _period_month: period.month });
      });
    });

    const sortedCategories = Object.entries(categoryData)
      .sort((a, b) => b[1].amount - a[1].amount);

    const toggleCategory = (cat: string) => {
      setExpandedCategories(prev => {
        const next = new Set(prev);
        if (next.has(cat)) next.delete(cat);
        else next.add(cat);
        return next;
      });
    };

    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

    return (
      <div className="p-6 lg:p-8">
        <div className="rounded-xl overflow-hidden" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
          <div className="px-6 py-4" style={{ background: 'var(--color-bg-elevated)', borderBottom: '1px solid var(--color-border)' }}>
            <h3 className="cs-h3" style={{ color: 'var(--color-text-primary)' }}>Expense Categories</h3>
            <p className="cs-meta mt-1">{categoriesCount} categories identified — click to expand</p>
          </div>
          <div style={{ borderColor: 'var(--color-border-faint)' }}>
            {sortedCategories.map(([category, catInfo]) => {
              const isExpanded = expandedCategories.has(category);
              // Show up to 50 transactions when expanded, sorted by date descending
              const displayTxns = isExpanded
                ? catInfo.transactions
                    .sort((a: any, b: any) => {
                      const dA = new Date(a._period_year, (a._period_month || 1) - 1, a.day || 1);
                      const dB = new Date(b._period_year, (b._period_month || 1) - 1, b.day || 1);
                      return dB.getTime() - dA.getTime();
                    })
                    .slice(0, 50)
                : [];
              return (
                <div key={category} style={{ borderTop: '1px solid var(--color-border-faint)' }}>
                  <button
                    onClick={() => toggleCategory(category)}
                    className="w-full flex items-center justify-between px-6 py-4 transition-colors text-left"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'var(--color-needs-human-tint)' }}>
                        <Folder className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />
                      </div>
                      <div>
                        <p className="cs-body font-medium" style={{ color: 'var(--color-text-primary)' }}>{category}</p>
                        <p className="cs-meta">{catInfo.count} transactions</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <p className="cs-figure font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                        ${catInfo.amount.toLocaleString(undefined, {maximumFractionDigits: 0})}
                      </p>
                      <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} style={{ color: 'var(--color-text-muted)' }} />
                    </div>
                  </button>
                  {isExpanded && (
                    <div className="overflow-x-auto" style={{ background: 'var(--color-bg-elevated)', borderTop: '1px solid var(--color-border-faint)' }}>
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="cs-label">
                            <th className="text-left px-4 py-2 font-medium">Date</th>
                            <th className="text-left px-3 py-2 font-medium">Description</th>
                            <th className="text-left px-3 py-2 font-medium">Type</th>
                            <th className="text-left px-3 py-2 font-medium">Source</th>
                            <th className="text-right px-3 py-2 font-medium">Confidence</th>
                            <th className="text-right px-4 py-2 font-medium">Amount</th>
                            <th className="text-center px-2 py-2 font-medium">Flags</th>
                          </tr>
                        </thead>
                        <tbody>
                          {displayTxns.map((txn: any, idx: number) => {
                            const expType = txn.expense_type || txn.classification?.type || '—';
                            const confidence = txn.classification?.confidence;
                            const sourceFile = txn.source_file ? txn.source_file.split('/').pop() : '';
                            const hasFlags = txn.anomaly || txn.needs_review;
                            const expTypeStyle = (
                              expType === 'revenue'
                                ? { background: 'transparent', color: 'var(--color-verified)' }
                                : expType === 'cogs' || expType === 'extra_expense'
                                ? { background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }
                                : { background: 'var(--color-bg-elevated)', color: 'var(--color-text-muted)' }
                            );
                            return (
                              <tr key={idx} style={{ borderTop: '1px solid var(--color-border-faint)', background: hasFlags ? 'var(--color-needs-human-tint)' : 'transparent' }}>
                                <td className="px-4 py-2 whitespace-nowrap" style={{ color: 'var(--color-text-secondary)' }}>
                                  {txn.date || txn.transaction_date || `${months[(txn._period_month || 1) - 1]} ${txn._period_year}`}
                                </td>
                                <td className="px-3 py-2 truncate max-w-[250px]" style={{ color: 'var(--color-text-primary)' }} title={txn.description || txn.memo || ''}>
                                  {txn.description || txn.memo || '—'}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap">
                                  <span className="cs-chip inline-flex items-center px-1.5 py-0.5 rounded" style={expTypeStyle}>
                                    {expType}
                                  </span>
                                </td>
                                <td className="px-3 py-2 truncate max-w-[150px] text-xs" style={{ color: 'var(--color-text-muted)' }} title={txn.source_file || ''}>
                                  {sourceFile || txn.source_type || '—'}
                                </td>
                                <td className="px-3 py-2 text-right whitespace-nowrap">
                                  {confidence != null ? (
                                    <span className="text-xs font-medium" style={{ color: confidence >= 0.8 ? 'var(--color-verified)' : 'var(--color-needs-human-text)' }}>
                                      {Math.round(confidence * 100)}%
                                    </span>
                                  ) : (
                                    <span className="text-xs" style={{ color: 'var(--color-text-dim)' }}>—</span>
                                  )}
                                </td>
                                <td className="px-4 py-2 text-right cs-figure font-medium whitespace-nowrap" style={{ color: (txn.amount || 0) > 0 ? 'var(--color-verified)' : 'var(--color-text-primary)' }}>
                                  {(txn.amount || 0) > 0 ? '+' : ''}${Math.abs(txn.amount || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                </td>
                                <td className="px-2 py-2 text-center whitespace-nowrap">
                                  {txn.anomaly && <span title="Anomaly detected" className="inline-block w-4 h-4 text-xs leading-4 rounded-full" style={{ background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>!</span>}
                                  {txn.needs_review && <span title="Needs review" className="inline-block w-4 h-4 text-xs leading-4 rounded-full ml-0.5" style={{ background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>?</span>}
                                  {!hasFlags && <span style={{ color: 'var(--color-text-dim)' }}>—</span>}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      {catInfo.count > 50 && (
                        <p className="text-center text-xs py-2" style={{ color: 'var(--color-text-dim)' }}>
                          Showing 50 of {catInfo.count} transactions
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // Render Tax Documents View
  // Helper to extract tax data from a tax file entry — handles both
  // file.data (legacy) and file.parsed_data (current upload pipeline)
  const getTaxData = (file: any) => {
    // Merge data and parsed_data — parsed_data may only have form_type while
    // data has the actual financial fields from reprocessing. Merge both so
    // financial fields are always accessible regardless of which field they're in.
    const d = file.data || {};
    const pd = file.parsed_data || {};
    return { ...d, ...pd, ...d }; // d wins for financial fields, pd fills gaps
  };

  const renderTaxView = () => {
    const taxFiles = tax1065Files.length > 0 ? tax1065Files : (data.tax1065Files || []);
    const form1065s = taxFiles.filter((f: any) => {
      const formType = f.form_type || f.parsed_data?.form_type || f.data?.form_type;
      return formType === '1065';
    });
    const k1Forms = taxFiles.filter((f: any) => {
      const formType = f.form_type || f.parsed_data?.form_type || f.data?.form_type;
      return formType === 'K-1' || formType === 'K1' || formType?.toLowerCase() === 'k-1';
    });

    return (
      <div className="p-6 lg:p-8">
        <div className="space-y-6">
          {taxFiles.length === 0 ? (
            <div className="text-center py-12 rounded-xl" style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)' }}>
              <FileText className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--color-text-dim)' }} />
              <p className="cs-body font-medium" style={{ color: 'var(--color-text-secondary)' }}>No tax documents uploaded yet</p>
              <p className="cs-meta mt-2">Upload 1065 or K-1 forms to see tax data</p>
              <button
                onClick={() => {
                  setIsUploadMode(true);
                  setActiveView('upload');
                }}
                className="mt-4 px-4 py-2 rounded-lg transition-colors text-sm font-medium"
                style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
              >
                Upload Tax Forms
              </button>
            </div>
          ) : (
            <>
              {/* Reprocess Tax Files Button */}
              {(() => {
                // Show reprocess when ANY of these are true:
                // 1. A file has no extracted financial data at all (old bug)
                // 2. A file is missing v3.0 fields (Lines 14/19 taxes/benefits,
                //    total_employee_compensation) — these populate AFTER an
                //    expanded-parser re-extraction and materially improve the
                //    payroll reconciliation variance.
                const missingCoreData = taxFiles.some((f: any) => {
                  const d = f.data || {};
                  return !d.ordinary_income && !d.gross_receipts && !d.partner_name && !d.guaranteed_payments;
                });
                const missingExpandedFields = taxFiles.some((f: any) => {
                  const d = f.data || {};
                  const ft = (d.form_type || f.form_type || '').toString().toLowerCase();
                  const is1065 = ft.includes('1065');
                  if (!is1065) return false;
                  // v3.0 fields: any of these missing means legacy extraction
                  return (
                    d.total_employee_compensation === undefined ||
                    d.taxes_licenses === undefined ||
                    d.employee_benefits === undefined
                  );
                });
                return (missingCoreData || missingExpandedFields);
              })() && (
                <div className="rounded-lg p-4 flex items-center justify-between" style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}>
                  <div>
                    <p className="cs-body font-medium" style={{ color: 'var(--color-needs-human-text)' }}>Tax files can be re-extracted with expanded parser</p>
                    <p className="cs-meta mt-0.5" style={{ color: 'var(--color-needs-human-text)' }}>
                      Pulls additional 1065 lines (COGS, employer taxes, employee benefits, guaranteed payments, total income)
                      to improve reconciliation accuracy.
                    </p>
                  </div>
                  <button
                    onClick={async () => {
                      const id = claimId || data?.id || data?.draft_id;
                      if (!id) {
                        setValidationError('No draft ID available for reprocessing');
                        return;
                      }
                      setReprocessingFileId('tax-reprocess');
                      try {
                        const response = await api.post('/api/v1/upload-savefirst/reprocess-tax-documents', {
                          draft_id: id,
                          force_reprocess: true
                        });
                        if (response?.success) {
                          const summary = response.summary;
                          setStatusMessage(`Reprocessed ${summary.processed} tax files (${summary.skipped} skipped, ${summary.failed} failed)`);
                          // Re-fetch draft data to pick up reprocessed tax files
                          try {
                            const draftResponse = await api.get(`/api/v1/reports/drafts/${id}`, { showErrorToast: false });
                            // draftResponse = { draft_id, step, data: { tax1065Files: [...] } }
                            const draftData = draftResponse?.data || draftResponse;
                            if (draftData?.tax1065Files?.length > 0) {
                              setTax1065Files(draftData.tax1065Files);
                              // Propagate to parent state so auto-save preserves reprocessed data
                              if (onDataUpdate) onDataUpdate({ tax1065Files: draftData.tax1065Files });
                              console.log(`✅ Loaded ${draftData.tax1065Files.length} tax files after reprocess`);
                            }
                          } catch (fetchErr) {
                            console.warn('Failed to refresh draft after reprocess:', fetchErr);
                          }
                        } else {
                          setValidationError(response?.message || 'Reprocessing failed');
                        }
                      } catch (err: any) {
                        setValidationError(`Reprocess failed: ${err.message}`);
                      } finally {
                        setReprocessingFileId(null);
                      }
                    }}
                    disabled={reprocessingFileId === 'tax-reprocess'}
                    className="px-4 py-2 rounded-lg transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-2 whitespace-nowrap"
                    style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
                  >
                    {reprocessingFileId === 'tax-reprocess' ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Reprocessing...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-4 h-4" />
                        Reprocess Tax Files
                      </>
                    )}
                  </button>
                </div>
              )}

              {/* Form 1065 Records — full Page-1 line coverage.
                  Some top-level flat fields are stored as 0 even when the
                  underlying extraction did grab a value (known flattening
                  bug: rent_expense / total_income / total_deductions).
                  read1065Line prefers a populated flat value but falls
                  through to taxData.page_1.line_*_* when flat is missing
                  or zero. */}
              {form1065s.length > 0 && (
                <div className="rounded-xl overflow-hidden" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
                  <div className="px-6 py-4" style={{ background: 'var(--color-bg-elevated)', borderBottom: '1px solid var(--color-border)' }}>
                    <h3 className="cs-h3 flex items-center gap-2" style={{ color: 'var(--color-needs-human-text)' }}>
                      <FileText className="w-5 h-5" />
                      Form 1065 - Partnership Return ({form1065s.length})
                    </h3>
                    <p className="cs-meta mt-1">
                      All Page-1 lines the extractor captures. Zero / unpopulated cells render as em-dash.
                    </p>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs whitespace-nowrap">
                      <thead style={{ background: 'var(--color-bg-elevated)' }}>
                        <tr>
                          <th className="text-left p-3 cs-label">Year</th>
                          <th className="text-right p-3 cs-label">Line 1c<br/>Gross Receipts</th>
                          <th className="text-right p-3 cs-label">Line 2<br/>COGS</th>
                          <th className="text-right p-3 cs-label">Line 3<br/>Gross Profit</th>
                          <th className="text-right p-3 cs-label">Line 8<br/>Total Income</th>
                          <th className="text-right p-3 cs-label">Line 9<br/>Salaries/Wages</th>
                          <th className="text-right p-3 cs-label">Line 10<br/>Guaranteed</th>
                          <th className="text-right p-3 cs-label">Line 13<br/>Rent</th>
                          <th className="text-right p-3 cs-label">Line 14<br/>Taxes/Lic.</th>
                          <th className="text-right p-3 cs-label">Line 19<br/>Empl. Benefits</th>
                          <th className="text-right p-3 cs-label">Line 21<br/>Total Deduct.</th>
                          <th className="text-right p-3 cs-label">Line 22<br/>Ordinary Inc.</th>
                        </tr>
                      </thead>
                      <tbody>
                        {form1065s.map((file: any, idx: number) => {
                          const taxData = getTaxData(file);
                          const page1 = taxData.page_1 || {};
                          // Prefer flat top-level, fall through to page_1.line_*
                          // when flat is null/undefined/0.  Works around the
                          // known flattening bug where rent_expense /
                          // total_income / total_deductions land at 0 even
                          // when page_1.line_* has the real value.
                          const read = (flat: number | undefined, nested: number | undefined): number => {
                            if (typeof flat === 'number' && flat !== 0) return flat;
                            if (typeof nested === 'number') return nested;
                            return flat ?? 0;
                          };
                          const line1c = read(taxData.gross_receipts, page1.line_1c_gross_receipts_net);
                          const line2 = read(taxData.cost_of_goods_sold, page1.line_2_cogs);
                          const line3 = page1.line_3_gross_profit ?? (line1c - line2);
                          const line8 = read(taxData.total_income, page1.line_8_total_income);
                          const line9 = read(taxData.salaries_wages ?? taxData.total_payroll, page1.line_9_salaries_wages);
                          // Canonical via helper — handles the known Line 22
                          // mislabel and prefers Schedule K Line 4 / Page 1 Line 10.
                          const line10 = resolveGuaranteedPayments(taxData) ?? page1.line_10_guaranteed_payments ?? 0;
                          const line13 = read(taxData.rent_expense, page1.line_13_rent);
                          const line14 = read(taxData.taxes_licenses, page1.line_14_taxes_licenses);
                          const line19 = read(taxData.employee_benefits, page1.line_19_employee_benefits);
                          const line21 = read(taxData.total_deductions, page1.line_21_total_deductions);
                          const line22 = read(taxData.ordinary_income, page1.line_22_ordinary_income);
                          return (
                            <tr key={idx} style={{ borderTop: '1px solid var(--color-border-faint)' }}>
                              <td className="p-3 cs-figure font-bold" style={{ color: 'var(--color-needs-human-text)' }}>
                                {taxData.tax_year || file.tax_year || file.year || '-'}
                              </td>
                              <td className="p-3 text-right cs-figure" style={{ color: 'var(--color-text-primary)' }}>{formatK1Value(line1c)}</td>
                              <td className="p-3 text-right cs-figure">{formatK1Value(line2)}</td>
                              <td className="p-3 text-right cs-figure">{formatK1Value(line3)}</td>
                              <td className="p-3 text-right cs-figure">{formatK1Value(line8)}</td>
                              <td className="p-3 text-right cs-figure font-bold" style={{ color: 'var(--color-needs-human-text)' }}>{formatK1Value(line9)}</td>
                              <td className="p-3 text-right cs-figure">{formatK1Value(line10)}</td>
                              <td className="p-3 text-right cs-figure">{formatK1Value(line13)}</td>
                              <td className="p-3 text-right cs-figure">{formatK1Value(line14)}</td>
                              <td className="p-3 text-right cs-figure">{formatK1Value(line19)}</td>
                              <td className="p-3 text-right cs-figure">{formatK1Value(line21)}</td>
                              <td className="p-3 text-right cs-figure font-bold" style={{ color: 'var(--color-text-primary)' }}>{formatK1Value(line22)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Schedule K-1 Records — all 12 reportable boxes per partner/year.
                  Non-operating boxes (2, 3, 5, 6, 7, 8, 9a, 11) feed the
                  reconciliation view's K-1 non-op income adjustment.  Zero
                  values render as em-dash for scannability. */}
              {k1Forms.length > 0 && (
                <div className="rounded-xl overflow-hidden" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
                  <div className="px-6 py-4" style={{ background: 'var(--color-bg-elevated)', borderBottom: '1px solid var(--color-border)' }}>
                    <h3 className="cs-h3 flex items-center gap-2" style={{ color: 'var(--color-verified)' }}>
                      <FileText className="w-5 h-5" />
                      Schedule K-1 - Partner's Share ({k1Forms.length})
                    </h3>
                    <p className="cs-meta mt-1">
                      All 12 reportable boxes. Non-operating boxes (2, 3, 5\u20138, 9a, 11) flow
                      to the reconciliation view so missing-deposit factors subtract them from
                      1065 gross-receipts coverage.
                    </p>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs whitespace-nowrap">
                      <thead style={{ background: 'var(--color-bg-elevated)' }}>
                        <tr>
                          <th className="text-left p-3 cs-label">Year</th>
                          <th className="text-left p-3 cs-label">Partner</th>
                          <th className="text-right p-3 cs-label">Box 1<br/>Ordinary</th>
                          <th className="text-right p-3 cs-label">Box 2<br/>Rental RE</th>
                          <th className="text-right p-3 cs-label">Box 3<br/>Other Rental</th>
                          <th className="text-right p-3 cs-label">Box 4<br/>Guaranteed</th>
                          <th className="text-right p-3 cs-label">Box 5<br/>Interest</th>
                          <th className="text-right p-3 cs-label">Box 6<br/>Dividends</th>
                          <th className="text-right p-3 cs-label">Box 7<br/>Royalties</th>
                          <th className="text-right p-3 cs-label">Box 8<br/>ST Cap Gain</th>
                          <th className="text-right p-3 cs-label">Box 9a<br/>LT Cap Gain</th>
                          <th className="text-right p-3 cs-label">Box 11<br/>Other Inc.</th>
                          <th className="text-right p-3 cs-label">Box 14<br/>SE Earn.</th>
                          <th className="text-right p-3 cs-label">Box 19<br/>Distrib.</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...k1Forms]
                          .sort((a: any, b: any) => {
                            const getYear = (f: any) => {
                              const td = getTaxData(f);
                              return Number(td.tax_year || f.tax_year || f.year || 0);
                            };
                            const getPartner = (f: any) => {
                              const td = getTaxData(f);
                              return (td.partner_name || (td.k1_data || {}).partner_name || '').toString();
                            };
                            const ya = getYear(a), yb = getYear(b);
                            if (ya !== yb) return ya - yb;
                            return getPartner(a).localeCompare(getPartner(b));
                          })
                          .map((file: any, idx: number) => {
                            const taxData = getTaxData(file);
                            const k1Data = taxData.k1_data || {};
                            // All box reads go through the canonical helper.
                            // The helper tries k1_data → schedule_k1.partners[0] →
                            // top-level → legacy aliases, in that order.  No more
                            // per-component fallback chains that drift.
                            const pick = (key: string) => resolveK1Box(taxData, key);
                            const fmt = formatK1Value;
                            const ordinaryIncome = pick('box_1_ordinary_income') ?? 0;
                            const guaranteedPay = pick('box_4_guaranteed_payments') ?? 0;
                            return (
                              <tr key={idx} style={{ borderTop: '1px solid var(--color-border-faint)' }}>
                                <td className="p-3 cs-figure font-bold" style={{ color: 'var(--color-verified)' }}>
                                  {taxData.tax_year || file.tax_year || file.year || '-'}
                                </td>
                                <td className="p-3" style={{ color: 'var(--color-text-secondary)' }}>
                                  {taxData.partner_name || k1Data.partner_name || 'Unknown'}
                                </td>
                                <td className="p-3 text-right cs-figure font-bold" style={{ color: ordinaryIncome < 0 ? 'var(--color-text-muted)' : 'var(--color-verified)' }}>
                                  {fmt(ordinaryIncome)}
                                </td>
                                <td className="p-3 text-right cs-figure" style={{ color: 'var(--color-text-muted)' }}>{fmt(pick('box_2_net_rental_real_estate'))}</td>
                                <td className="p-3 text-right cs-figure" style={{ color: 'var(--color-text-muted)' }}>{fmt(pick('box_3_other_rental_income'))}</td>
                                <td className="p-3 text-right cs-figure">{fmt(guaranteedPay)}</td>
                                <td className="p-3 text-right cs-figure" style={{ color: 'var(--color-text-muted)' }}>{fmt(pick('box_5_interest'))}</td>
                                <td className="p-3 text-right cs-figure" style={{ color: 'var(--color-text-muted)' }}>{fmt(pick('box_6_dividends'))}</td>
                                <td className="p-3 text-right cs-figure" style={{ color: 'var(--color-text-muted)' }}>{fmt(pick('box_7_royalties'))}</td>
                                <td className="p-3 text-right cs-figure" style={{ color: 'var(--color-text-muted)' }}>{fmt(pick('box_8_net_st_capital_gain'))}</td>
                                <td className="p-3 text-right cs-figure" style={{ color: 'var(--color-text-muted)' }}>{fmt(pick('box_9a_net_lt_capital_gain'))}</td>
                                <td className="p-3 text-right cs-figure" style={{ color: 'var(--color-text-muted)' }}>{fmt(pick('box_11_other_income'))}</td>
                                <td className="p-3 text-right cs-figure">{fmt(pick('box_14_self_employment_earnings'))}</td>
                                <td className="p-3 text-right cs-figure">{fmt(pick('box_19_distributions'))}</td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  // Render Revenue Sources View — third-party merchant / processor
  // settlement statements.  Sibling to Tax Docs: parses settlement
  // statements, auto-flips processor checkboxes in the card above, and
  // runs the on-demand reconciliation that explains the Bank-vs-1065 gap.
  const renderRevenueSourcesView = () => {
    const statements = (data.revenue_source_statements || []) as any[];
    const reconciliation = data.revenue_source_reconciliation || null;

    // Group statements by processor, then by year, for a browsable list
    // that mirrors the Tax Docs layout (1065s grouped by year, K-1s by
    // partner+year).
    const byProcessor: Record<string, Record<number, any[]>> = {};
    for (const env of statements) {
      const proc = env?.parsed?.processor_name || 'Unknown';
      const yr = env?.parsed?.statement_period_year;
      if (!yr) continue;
      byProcessor[proc] = byProcessor[proc] || {};
      byProcessor[proc][yr] = byProcessor[proc][yr] || [];
      byProcessor[proc][yr].push(env);
    }
    const processors = Object.keys(byProcessor).sort();

    // Em-dash for zero / null matches the financial-data convention used
    // by the 1065 + K-1 tables.
    const fmt = (n: number | null | undefined) => {
      if (n === null || n === undefined || n === 0) return '—';
      const neg = n < 0;
      const abs = Math.abs(n).toLocaleString('en-US', { maximumFractionDigits: 0 });
      return neg ? `($${abs})` : `$${abs}`;
    };

    return (
      <div className="p-6 h-full overflow-y-auto space-y-6">
        <div>
          <h2 className="cs-h2">Revenue Sources</h2>
          <p className="cs-meta mt-1">
            Third-party processor settlement statements (Stripe, DoorDash, Uber Eats, Grubhub, Square, Toast, etc.)
            and state sales tax.  Closes the Bank vs 1065 gross-receipts gap when the client's primary bank
            account doesn't capture delivery / online-order settlements.
          </p>
        </div>

        <RevenueSourcesCard
          value={data.revenue_sources as RevenueSourcesData | undefined}
          onChange={(next) => onDataUpdate?.({ ...data, revenue_sources: next })}
          claimId={claimId}
          clientEmail={data.claimant_email || data.client_email || clientEmail}
          statements={statements}
          reconciliation={reconciliation}
          onReconciled={(summary) => onDataUpdate?.({
            ...data,
            revenue_source_reconciliation: summary,
            revenue_source_statements: statements.map((s: any) => ({
              ...s,
              reconciled: true,
              reconciliation_run_at: summary.run_at,
            })),
          })}
        />

        {statements.length === 0 && (
          <div className="rounded-xl p-8 text-center text-sm" style={{ border: '1px dashed var(--color-border)', background: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}>
            <p className="font-medium mb-1" style={{ color: 'var(--color-text-primary)' }}>No merchant statements uploaded yet</p>
            <p>
              Upload processor settlement statements via the main wizard uploader. The AI classifier
              routes them to the merchant parser automatically — no dropdown selection needed.
              Parsed platforms auto-flip the processor checkboxes above, and reconciliation
              against 1065 + bank deposits runs hands-off at the end of the upload.
            </p>
          </div>
        )}

        {processors.map((proc) => {
          const years = byProcessor[proc];
          const yearKeys = Object.keys(years).map(Number).sort();
          // Roll up totals for the header line — shown across all years.
          let procTotalGross = 0;
          let procTotalNet = 0;
          let procStatementCount = 0;
          let procAchCount = 0;
          const platformSet = new Set<string>();
          for (const yr of yearKeys) {
            for (const env of years[yr]) {
              const ss = env?.parsed?.settlement_summary || {};
              procTotalGross += ss.total_gross_sales || 0;
              procTotalNet += ss.total_net_payout || 0;
              procStatementCount += 1;
              procAchCount += (env?.parsed?.ach_payouts || []).length;
              for (const row of (env?.parsed?.platform_breakdown || [])) {
                if (row.platform) platformSet.add(row.platform);
              }
            }
          }
          return (
            <div key={proc} className="rounded-xl overflow-hidden" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
              <div className="px-6 py-4" style={{ background: 'var(--color-bg-elevated)', borderBottom: '1px solid var(--color-border)' }}>
                <h3 className="cs-h3 flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
                  <Store className="w-5 h-5" style={{ color: 'var(--color-text-secondary)' }} />
                  {proc}
                </h3>
                <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-1 cs-meta" style={{ color: 'var(--color-text-muted)' }}>
                  <span>{procStatementCount} statement{procStatementCount === 1 ? '' : 's'}</span>
                  <span><span className="cs-figure" style={{ color: 'var(--color-text-primary)' }}>{fmt(procTotalGross)}</span> gross</span>
                  <span><span className="cs-figure" style={{ color: 'var(--color-text-primary)' }}>{fmt(procTotalNet)}</span> net</span>
                  <span><span className="cs-figure" style={{ color: 'var(--color-text-primary)' }}>{procAchCount}</span> ACH payouts</span>
                </div>
                {platformSet.size > 0 && (
                  <p className="cs-meta mt-1.5" style={{ color: 'var(--color-text-muted)' }}>
                    Platforms: {Array.from(platformSet).sort().join(', ')}
                  </p>
                )}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead style={{ background: 'var(--color-bg-elevated)' }}>
                    <tr>
                      <th className="text-left p-3 cs-label">Year</th>
                      <th className="text-left p-3 cs-label">Period</th>
                      <th className="text-right p-3 cs-label">Gross sales</th>
                      <th className="text-right p-3 cs-label">Fees</th>
                      <th className="text-right p-3 cs-label">Net payout</th>
                      <th className="text-right p-3 cs-label">Platforms</th>
                      <th className="text-right p-3 cs-label">ACH rows</th>
                      <th className="text-right p-3 cs-label">Status</th>
                    </tr>
                  </thead>
                  <tbody className="font-mono" style={{ color: 'var(--color-text-secondary)' }}>
                    {yearKeys.map((yr) => (
                      years[yr]
                        .sort((a: any, b: any) =>
                          (a.parsed?.statement_period_month || 0) - (b.parsed?.statement_period_month || 0))
                        .map((env: any, idx: number) => {
                          const p = env.parsed || {};
                          const ss = p.settlement_summary || {};
                          const month = p.statement_period_month;
                          const periodLabel = month
                            ? `${yr}-${String(month).padStart(2, '0')}`
                            : yr;
                          const reconciled = env.reconciled;
                          const zero = p.zero_activity_flag;
                          return (
                            <tr key={`${yr}-${idx}`} style={{ borderTop: '1px solid var(--color-border-faint)' }}>
                              <td className="p-3 font-sans font-semibold">{yr}</td>
                              <td className="p-3 font-sans">{periodLabel}</td>
                              <td className="p-3 text-right">{fmt(ss.total_gross_sales)}</td>
                              <td className="p-3 text-right">{fmt(ss.total_platform_fees)}</td>
                              <td className="p-3 text-right">{fmt(ss.total_net_payout)}</td>
                              <td className="p-3 text-right font-sans">
                                {(p.platform_breakdown || []).length}
                              </td>
                              <td className="p-3 text-right font-sans">
                                {(p.ach_payouts || []).length}
                              </td>
                              <td className="p-3 text-right font-sans">
                                {zero ? (
                                  <span className="text-xs font-semibold" style={{ color: 'var(--color-needs-human-text)' }}>zero-activity</span>
                                ) : reconciled ? (
                                  <span className="text-xs font-semibold" style={{ color: 'var(--color-verified)' }}>analyzed</span>
                                ) : (
                                  <span className="text-xs font-semibold" style={{ color: 'var(--color-needs-human-text)' }}>received</span>
                                )}
                              </td>
                            </tr>
                          );
                        })
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Render Reconciliation View - P&L vs 1065 Cross-Check
  const renderReconciliationView = () => {
    // Get tax files data
    const taxFiles = tax1065Files || [];
    const form1065s = taxFiles.filter((f: any) => f.form_type === '1065');
    const k1Forms = taxFiles.filter((f: any) => f.form_type === 'K-1' || f.form_type === 'K1');
    
    return (
      <div className="p-6 h-full overflow-y-auto">
        <div className="mb-6">
          <h2 className="cs-h2">P&L ↔ 1065 Reconciliation</h2>
          <p className="cs-meta mt-1">
            Cross-check bank-derived P&L data against tax-reported 1065 figures
          </p>
        </div>
        
        <TaxReconciliation
          plData={aiExtractedData}
          tax1065Files={form1065s}
          k1Forms={k1Forms}
          varianceExplanations={hookCanonicalData?.variance_explanations}
          onNavigateToRevenueSources={() => setActiveView('revenue_sources')}
        />
      </div>
    );
  };

  // Render Anomalies View
  const renderAnomaliesView = () => {
    // Try multiple sources for canonical data: local state, data prop, or build from aiExtractedData
    const canonicalData = unifiedUploadData?.canonical_data 
      || data.unified_upload_data?.canonical_data
      || (aiExtractedData ? buildCanonicalFromFinancialData(aiExtractedData) : null);
    
    // Transform anomalies for the enhanced resolver
    const transformedAnomalies = cachedAnomalyAnalysis?.anomalies?.map((a: any) => {
      // Calculate deviation and amounts from anomaly data
      const deviation = a.deviation_percent || 0;
      const relatedAmount = a.related_amount || 0;
      const isSpike = deviation > 0;
      
      // Extract period info
      const period = a.affected_period || '';
      const [year, month] = period.split('-').map(Number);
      
      // Find surrounding periods for from/to values
      const periods = canonicalData?.periods || [];
      const currentPeriodIdx = periods.findIndex((p: any) => p.year === year && p.month === month);
      const currentPeriod = periods[currentPeriodIdx];
      const prevPeriod = periods[currentPeriodIdx - 1];
      
      const fromValue = prevPeriod?.gross_revenue || 0;
      const toValue = currentPeriod?.gross_revenue || relatedAmount;
      
      // Build category breakdown if transactions are available
      const categoryBreakdown: any[] = [];
      if (currentPeriod?.transactions && prevPeriod?.transactions) {
        const currCategories = new Map<string, number>();
        const prevCategories = new Map<string, number>();
        
        currentPeriod.transactions.forEach((t: any) => {
          const cat = t.category || 'Uncategorized';
          currCategories.set(cat, (currCategories.get(cat) || 0) + Math.abs(t.amount || 0));
        });
        
        prevPeriod.transactions.forEach((t: any) => {
          const cat = t.category || 'Uncategorized';
          prevCategories.set(cat, (prevCategories.get(cat) || 0) + Math.abs(t.amount || 0));
        });
        
        // Get top 5 categories by change
        const allCategories = new Set([...currCategories.keys(), ...prevCategories.keys()]);
        allCategories.forEach(cat => {
          const prev = prevCategories.get(cat) || 0;
          const curr = currCategories.get(cat) || 0;
          if (Math.abs(curr - prev) > 0) {
            categoryBreakdown.push({
              name: cat,
              prev,
              curr,
              delta: curr - prev
            });
          }
        });
        
        categoryBreakdown.sort((x, y) => Math.abs(y.delta) - Math.abs(x.delta));
      }
      
      // Get top transactions for this anomaly period
      // If anomaly has transaction_ids, use those; otherwise get top transactions by amount from the period
      let topTransactions: any[] = [];
      
      if (a.transactions && a.transactions.length > 0) {
        // Use pre-computed transactions from backend (new anomalies)
        topTransactions = a.transactions;
      } else if (currentPeriod?.transactions && currentPeriod.transactions.length > 0) {
        // Augment with top transactions from period data (for old anomalies missing transaction data)
        // Sort by absolute amount to get the most significant transactions
        const sortedTxns = [...currentPeriod.transactions]
          .filter((t: any) => t.amount !== 0)
          .sort((x: any, y: any) => Math.abs(y.amount || 0) - Math.abs(x.amount || 0));
        
        topTransactions = sortedTxns.slice(0, 5).map((txn: any) => ({
          id: txn.id || txn._txn_id,
          date: txn.transaction_date || txn.date || period,
          description: (txn.description || txn.payee || 'Transaction').slice(0, 60),
          amount: txn.amount || 0,
          category: txn.category || 'Uncategorized',
          flag: Math.abs(txn.amount || 0) > 5000 // Flag large transactions
        }));
      } else if (a.transaction_ids && a.transaction_ids.length > 0) {
        // Fallback: try to find by transaction_ids
        topTransactions = a.transaction_ids
          .slice(0, 5)
          .map((txnId: string) => {
            const txn = currentPeriod?.transactions?.find((t: any) => 
              t.id === txnId || t._txn_id === txnId
            );
            return txn ? {
              id: txn.id || txn._txn_id,
              date: txn.transaction_date || txn.date || period,
              description: (txn.description || txn.payee || 'Transaction').slice(0, 60),
              amount: txn.amount || 0,
              category: txn.category || 'Uncategorized',
              flag: true
            } : null;
          })
          .filter(Boolean);
      }
      
      return {
        id: a.id,
        severity: a.severity || 'medium',
        type: a.anomaly_type?.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()) || 'Revenue Change',
        title: a.title || a.description,
        description: a.description || '',
        percent: Math.abs(deviation),
        from: fromValue,
        to: toValue,
        periodFrom: prevPeriod ? `${prevPeriod.year}-${String(prevPeriod.month).padStart(2, '0')}` : '',
        periodTo: period,
        period: period,
        deviation: deviation,
        categories: a.categories || categoryBreakdown.slice(0, 5),
        transactions: topTransactions,
        aiSuggestion: a.ai_analysis?.explanation || a.explanation,
        aiConfidence: a.ai_analysis?.confidence,
        sourceDoc: a.source_document,
        lossEvent: a.anomaly_type === 'revenue_drop' && Math.abs(deviation) > 50,
        is_resolved: a.is_resolved,
        resolution_note: a.resolution_note,
        resolved_at: a.resolved_at,
        resolution_reason: a.resolution_reason,
        anomaly_type: a.anomaly_type,
        explanation: a.explanation,
        ai_analysis: a.ai_analysis,
        forensic_insight: a.forensic_insight,
        forensic_analysis: a.forensic_analysis
      };
    }) || [];
    
    // Build periods data for timeline
    const periodsData = canonicalData?.periods?.map((p: any) => {
      const month = `${p.year}-${String(p.month).padStart(2, '0')}`;
      const hasAnomaly = transformedAnomalies.some((a: any) => a.period === month && !a.is_resolved);
      const anomalyType = transformedAnomalies.find((a: any) => a.period === month)?.deviation;
      return {
        month,
        revenue: p.gross_revenue || 0,
        anomaly: hasAnomaly ? (anomalyType && anomalyType < 0 ? 'drop' : 'spike') as 'spike' | 'drop' : undefined
      };
    }) || [];
    
    // Calculate totals
    const totalTransactions = canonicalData?.periods?.reduce((sum: number, p: any) => 
      sum + (p.transactions?.length || 0), 0) || 0;
    const totalMonths = canonicalData?.periods?.length || 0;
    
    // Handle batch resolve
    const handleBatchResolve = async (ids: string[], reason: string, note: string) => {
      for (const id of ids) {
        await handleResolveAnomaly(id, `${reason}: ${note}`);
      }
    };
    
    return (
      <div className="p-6 lg:p-8">
        {canonicalData?.periods?.length > 0 ? (
          transformedAnomalies.length > 0 ? (
            <AnomalyResolver
              anomalies={transformedAnomalies}
              periodsData={periodsData}
              totalTransactions={totalTransactions}
              totalMonths={totalMonths}
              onResolve={(id, reason, note) => handleResolveAnomaly(id, `${reason}: ${note}`)}
              onBatchResolve={handleBatchResolve}
              onUnresolve={handleUnresolveAnomaly}
              onViewTransaction={(txnId) => {
                setHighlightedTransactionId(txnId);
                setActiveView('transactions');
              }}
              onForensicInsightFetched={handleForensicInsightFetched}
            />
          ) : (
            <AnomalyBanner
              canonicalData={canonicalData}
              resolvedAnomalies={resolvedAnomalies}
              draftId={claimId}
              businessContext={{
                business_type: data.business_type,
                industry: data.naics_code,
                event_type: data.event_type,
                event_start: eventStartDate,
                event_end: eventEndDate
              }}
              enableAIAnalysis={true}
              cachedAnalysis={cachedAnomalyAnalysis}
              onAnalysisComplete={handleAnalysisComplete}
              onViewTransaction={(txnId) => {
                setHighlightedTransactionId(txnId);
                setActiveView('transactions');
              }}
              onResolveAnomaly={handleResolveAnomaly}
              onUnresolveAnomaly={handleUnresolveAnomaly}
            />
          )
        ) : (
          <div className="text-center py-12 rounded-xl" style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)' }}>
            <AlertTriangle className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--color-text-dim)' }} />
            <p className="cs-body font-medium" style={{ color: 'var(--color-text-secondary)' }}>No data to analyze</p>
            <p className="cs-meta mt-2">Upload financial documents first</p>
          </div>
        )}
      </div>
    );
  };

  // Render active view content
  // Wrapper to add DocumentChecklist to applicable views
  const withDocumentChecklist = (content: React.ReactNode, showChecklist: boolean = true) => {
    if (!showChecklist || !claimId) {
      return content;
    }
    return (
      <div className="space-y-6">
        {/* Document Requirements Checklist - Hard Gate for Accountant Queue */}
        <DocumentChecklist 
          caseId={claimId}
          compact={activeView !== 'summary'} // Compact mode except for summary
          onReadinessChange={(ready) => {
            console.log('📋 Document readiness changed:', ready);
          }}
        />
        {content}
      </div>
    );
  };

  const renderContent = () => {
    // Method selection screen - no checklist here
    if (entryMethod === 'select') {
      return renderMethodSelection();
    }
    
    // Template mode - show checklist
    if (entryMethod === 'template' || activeView === 'template') {
      return withDocumentChecklist(renderTemplateView());
    }
    
    // Template complete view - show checklist
    if (activeView === 'template_complete') {
      return withDocumentChecklist(renderTemplateComplete());
    }
    
    // Upload mode - show checklist (important - users need to see what's missing)
    if (isUploadMode || activeView === 'upload') {
      return withDocumentChecklist(renderUploadMode());
    }
    
    // Files view - show uploaded S3 files with checklist
    if (activeView === 'files') {
      return withDocumentChecklist(renderFilesView());
    }
    
    switch (activeView) {
      case 'summary':
        // Summary already has checklist integrated at top, skip wrapper
        return renderSummaryView();
      case 'monthly':
        return withDocumentChecklist(renderMonthlyView());
      case 'transactions':
        return withDocumentChecklist(renderTransactionsView());
      case 'categories':
        return withDocumentChecklist(renderCategoriesView());
      case 'tax':
        return withDocumentChecklist(renderTaxView());
      case 'revenue_sources':
        return withDocumentChecklist(renderRevenueSourcesView());
      case 'reconciliation':
        return withDocumentChecklist(renderReconciliationView());
      case 'anomalies':
        return withDocumentChecklist(renderAnomaliesView());
      default:
        return renderSummaryView();
    }
  };

  // Determine subtitle based on mode
  const getSubtitle = () => {
    if (entryMethod === 'select') {
      return "Choose how you'd like to enter your financial data";
    }
    if (entryMethod === 'template' || activeView === 'template') {
      return "Select your industry and enter revenue to generate P&L estimates";
    }
    if (activeView === 'template_complete') {
      return `${appliedTemplate?.template_name || 'Industry Template'} - P&L generated from benchmarks`;
    }
    if (isUploadMode) {
      return "Upload your business financial documents for AI-powered analysis";
    }
    return `Reviewing ${totalTransactions.toLocaleString()} transactions across ${totalPeriods} months`;
  };

  // Handle next for template mode
  const handleTemplateNext = () => {
    const template = appliedTemplate || data.applied_template;
    
    if (!eventStartDate || !eventEndDate) {
      setValidationError('Please enter the Event Coverage Period dates before continuing.');
      return;
    }
    
    // Debug logging
    console.log('🔄 handleTemplateNext - current data.calculation_mode:', data.calculation_mode);
    console.log('🔄 handleTemplateNext - template:', template);
    console.log('🔄 handleTemplateNext - financial_data:', template?.financial_data || aiExtractedData);
    
    const nextData = {
      ...data,
      financial_data: template?.financial_data || aiExtractedData,
      fromTemplate: true,
      applied_template: template,
      template_id: template?.template_id,
      template_name: template?.template_name,
      event_start_date: persistableEventStartDate(eventStartDate, startDatePrefilled, data),
      event_end_date: eventEndDate,
      fema_declaration_number: femaDeclarationNumber,
      // Explicitly preserve calculation_mode
      calculation_mode: data.calculation_mode,
    };
    
    console.log('🔄 handleTemplateNext - nextData to pass:', nextData);
    console.log('🔄 handleTemplateNext - calculation_mode being passed:', nextData.calculation_mode);
    
    onNext(nextData);
  };

  // Handle Quick Actions from sidebar
  const handleQuickAction = (action: string) => {
    console.log('⚡ Quick Action triggered:', action);
    switch (action) {
      case 'upload':
        // Switch to upload view
        setActiveView('upload');
        setIsUploadMode(true);
        break;
      case 'clarification':
        // Switch to transactions view where user can flag items for clarification
        if (hasFinancialData) {
          setActiveView('transactions');
          setIsUploadMode(false);
        } else {
          // If no financial data, prompt to upload first
          setActiveView('upload');
          setIsUploadMode(true);
        }
        break;
      default:
        console.log('Unknown quick action:', action);
    }
  };

  // shouldShowMethodSelection is now computed early in the component (after entryMethod)

  return (
    <WizardV2Layout
      currentStep={3}
      highestCompletedStep={highestCompletedStep}
      completionPercent={backendCompletionPercent}
      stepTitle="Financial Data"
      stepSubtitle={getSubtitle()}
      calculationMode={data.calculation_mode || 'standard_bi'}
      sections={shouldShowMethodSelection ? [] : buildSections()}
      qaItems={shouldShowMethodSelection ? [] : buildQAItems()}
      nextAction={shouldShowMethodSelection ? undefined : (!hasFinancialData ? {
        label: 'Upload financial documents',
        description: 'Required to continue'
      } : itemsNeedingReview > 0 ? {
        label: `Review ${itemsNeedingReview} items`,
        description: 'Recommended before continuing'
      } : undefined)}
      isSaving={isSaving}
      lastSaved={lastSaved}
      onSave={onSave}
      goToStep={goToStep}
      onBack={onBack}
      onNext={activeView === 'template_complete' ? handleTemplateNext : handleNext}
      claimId={claimId}
      chromeless={chromeless}
      nextLabel={chromeless ? "Continue to Payroll" : "Continue to Date Selection"}
      isNextDisabled={!hasFinancialData || (activeView === 'template_complete' && (!eventStartDate || !eventEndDate))}
      showNext={!isUploadMode && !shouldShowMethodSelection && (hasFinancialData || activeView === 'template_complete')}
      onQuickAction={handleQuickAction}
    >
      {validationError && (
        <p className="text-sm mb-4 px-6" style={{ color: 'var(--color-needs-human-text)' }}>{validationError}</p>
      )}
      {statusMessage && (
        <p className="text-sm mb-4 px-6" style={{ color: 'var(--color-verified)' }}>{statusMessage}</p>
      )}
      {renderContent()}
    </WizardV2Layout>
  );
}
